/**
 * Client for the ODIN control API.
 *
 * The API base URL comes from VITE_CONTROL_API_URL at build time. Requests send
 * cookies (`credentials: "include"`) so the HttpOnly session cookie authenticates
 * the user. No Discord OAuth token or bot token is ever stored in the browser —
 * the dashboard only POSTs a bot token once and never persists it.
 */
import { apiBaseUrl, statusUrl } from "./config";

const API_BASE = apiBaseUrl;

export const apiConfigured = API_BASE.length > 0;

/** Whether a public status source is reachable (a dedicated status URL or the API). */
export const statusConfigured = statusUrl.length > 0;

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  // Only advertise a JSON body when we actually send one. Setting `content-type:
  // application/json` on a body-less POST/DELETE makes Fastify reject the request
  // with "Body cannot be empty when content-type is set to 'application/json'"
  // (e.g. the customer portal, BALDER cancel/confirm, moderation actions).
  const hasBody = init.body !== undefined && init.body !== null;
  const res = await fetch(`${API_BASE}${path}`, {
    ...init,
    credentials: "include",
    headers: {
      accept: "application/json",
      ...(hasBody ? { "content-type": "application/json" } : {}),
      ...(init.headers ?? {}),
    },
  });
  if (res.status === 204) return undefined as T;
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = (body as { error?: { code?: string; message?: string } }).error;
    throw new ApiError(res.status, err?.code ?? "error", err?.message ?? `Request failed (${res.status})`);
  }
  return body as T;
}

/**
 * Fetch an absolute URL as JSON (used for the PUBLIC status endpoint, which may be
 * a separate, edge-cached, cross-origin URL). No credentials are sent — the public
 * status projection is secret-free by construction on the server.
 */
async function requestAbsolute<T>(url: string): Promise<T> {
  const res = await fetch(url, { headers: { accept: "application/json" } });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err = (body as { error?: { code?: string; message?: string } }).error;
    throw new ApiError(res.status, err?.code ?? "error", err?.message ?? `Request failed (${res.status})`);
  }
  return body as T;
}

// ── Types ────────────────────────────────────────────────────────────────────
export interface PlatformStatus {
  service: string;
  environment: string;
  safety: { dryRun: boolean; allowLiveDeploy: boolean; requireApproval: boolean; liveMutationsAllowed: boolean };
  liveDeploymentLocked: boolean;
  discordStagingConfigured: boolean;
}
export interface Me {
  id: string;
  discordUserId: string;
  username: string;
  displayName: string | null;
  avatarUrl: string | null;
  platformRole: string;
}
export interface Workspace {
  id: string;
  slug: string;
  name: string;
  role?: string;
}
export interface WorkspaceMember {
  userId: string;
  role: string;
  username: string;
}

/**
 * What deleting a workspace would cost, answered by the server before anything
 * is done.
 *
 * `consequences` is prose the server writes, not a template the browser fills
 * in. Only the server knows how many snapshots exist or which Discord servers
 * are attached, and a confirmation dialog that guesses is not a confirmation.
 */
export interface WorkspaceDeletionBlocker {
  code: "not_owner" | "active_subscription" | "deployment_in_flight" | string;
  message: string;
  resolution: string;
}
export interface WorkspaceDeletionFacts {
  guilds: Array<{ discordGuildId: string; name: string }>;
  snapshots: number;
  blueprints: number;
  balderConversations: number;
  supportTickets: number;
  deployments: number;
  inFlightDeployments: Array<{ id: string; status: string }>;
  subscription: {
    planCode: string;
    status: string;
    providerSubscriptionId: string | null;
    cancelAtPeriodEnd: boolean;
  } | null;
  owners: Array<{ userId: string; username: string }>;
  memberCount: number;
}
export interface WorkspaceDeletionPreview {
  tenantId: string;
  name: string;
  slug: string;
  /** What the customer must type back. Always the workspace name. */
  confirmationPhrase: string;
  owners: Array<{ userId: string; username: string }>;
  facts: WorkspaceDeletionFacts;
  consequences: string[];
  preserved: string[];
  blockers: WorkspaceDeletionBlocker[];
  canDelete: boolean;
  isFinalWorkspace: boolean;
  replacementWorkspaceName: string | null;
}
export interface WorkspaceDeletionResult {
  status: string;
  tenantId: string;
  name: string;
  /** The empty workspace ODIN created when this was the customer's last one. */
  replacement: Workspace | null;
  purged: Record<string, number>;
}
export interface DiscordGuild {
  discordGuildId: string;
  name: string;
  iconUrl: string | null;
  owner: boolean;
  canManage: boolean;
  alreadyConnected: boolean;
  connectedTenantId: string | null;
}

/**
 * The server-side server list.
 *
 * The Servers page used to decide what was connected from `localStorage`, so a
 * server connected on a laptop was invisible on a phone and a server ODIN was
 * genuinely installed in never appeared at all. This is the reconciled answer:
 * ODIN's own records, checked against Discord's view of the bot and of the
 * customer, with `sources` recording anything that could not be checked.
 */
export type ServerConnectionState =
  | "connected"
  | "awaiting_bot"
  | "bot_removed"
  | "installed_not_connected"
  | "claimed_elsewhere"
  /** This workspace asked ODIN to stop. The history is kept; reconnecting restores it. */
  | "disconnected";

export interface ServerHealthFinding {
  code: string;
  severity: "ok" | "degraded" | "blocked";
  summary: string;
  detail: string;
}
export interface ServerHealth {
  status: string;
  severity: "ok" | "degraded" | "blocked";
  findings: ServerHealthFinding[];
}
export interface ServerDirectoryEntry {
  guildId: string | null;
  discordGuildId: string;
  name: string;
  iconUrl: string | null;
  state: ServerConnectionState;
  claimedByTenantId: string | null;
  /** `null` means "not known", which must render as "—" and never as 0. */
  memberCount: number | null;
  /**
   * ISO timestamp of the last successful live read of this server, or `null`
   * if ODIN has never asked. Rendered as "—", never as "just now": "never
   * asked" and "asked a moment ago" are opposite facts about how much the rest
   * of this row can be trusted.
   */
  lastRefreshedAt: string | null;
  /** Kept beside it so "refreshed" is never read as "snapshotted". */
  lastSnapshotAt: string | null;
  canManage: boolean;
  canConnect: boolean;
  health: ServerHealth;
}
export interface ServerDirectorySource {
  source: "workspace" | "bot_installations" | "discord_account" | "bot_application";
  status: "ok" | "unavailable" | "reauth_required" | "not_configured";
  detail: string;
}
export interface ServerDirectory {
  servers: ServerDirectoryEntry[];
  sources: ServerDirectorySource[];
}

/**
 * "Refresh server" — what Discord says about a server at the moment it is asked.
 *
 * Everything else on the Servers page is ODIN's own record, written whenever the
 * server was connected. A customer who renames a channel, adds a role, or grants
 * the permission ODIN complained about had no way to make ODIN look again, and
 * no way to tell whether what they were reading was current or a month old.
 *
 * The structure below is for display only. It is not stored anywhere on the
 * client and the server does not store it either — a refresh deliberately does
 * not create a snapshot, which is why `snapshotCreated` is typed as the literal
 * `false` rather than `boolean`.
 */
export type ServerChannelKind =
  | "text"
  | "announcement"
  | "forum"
  | "voice"
  | "stage"
  | "category"
  | "rules"
  | "media"
  | "directory"
  | "unknown";

export interface RefreshedRole {
  id: string;
  name: string;
  position: number;
  color: number;
  managed: boolean;
  hoist: boolean;
  mentionable: boolean;
  /** The @everyone role, which shares the guild's id and cannot be deleted. */
  isEveryone: boolean;
  /**
   * Whether ODIN's own role sits above this one. Discord refuses a bot any role
   * at or above its own position, regardless of permissions — so this is the
   * difference between a role ODIN can assign and one it will be refused on.
   */
  odinCanManage: boolean;
}
export interface RefreshedChannel {
  id: string;
  name: string;
  type: ServerChannelKind;
  parentId: string | null;
  position: number;
  overwriteCount: number;
  /** Whether ODIN can see this channel once overwrites are applied. */
  visibleToOdin: boolean;
}
export interface RefreshCounts {
  roles: number;
  categories: number;
  channels: number;
  overwrites: number;
}
export interface RefreshFailure {
  code: string;
  /** One customer-facing line, written server-side. Safe to render verbatim. */
  summary: string;
  /** What to do about it. Also customer-facing. */
  detail: string;
  /** Discord's own backoff, when it gave one. `null` means it did not say. */
  retryAfterSeconds: number | null;
}
export interface GuildRefreshResult {
  guildId: string;
  discordGuildId: string;
  /** `false` means Discord did not answer, and everything below is the old data. */
  ok: boolean;
  name: string;
  iconUrl: string | null;
  memberCount: number | null;
  /** Only advances on success, so it never overstates how fresh this is. */
  lastRefreshedAt: string | null;
  lastSnapshotAt: string | null;
  roles: RefreshedRole[];
  categories: RefreshedChannel[];
  channels: RefreshedChannel[];
  counts: RefreshCounts;
  /** `null` when the failure tells us nothing trustworthy about health. */
  health: ServerHealth | null;
  error: RefreshFailure | null;
  /** True when this answer came from a refresh that was already running. */
  coalesced: boolean;
  snapshotCreated: false;
}

/** What ODIN is holding for a server, so "delete it" is an informed choice. */
export interface RetainedGuildData {
  snapshots: number;
  deployments: number;
  backups: number;
  moderationCases: number;
  tickets: number;
}
export interface DisconnectPreview {
  guildId: string;
  name: string;
  /** `null` means Discord did not answer — render it as unknown, never as "no". */
  botStillInDiscord: boolean | null;
  retained: RetainedGuildData;
  consequences: string[];
  canPurgeData: boolean;
  canRemoveFromDiscord: boolean;
}
export interface DisconnectResult {
  guildId: string;
  dataDeleted: boolean;
  botStillInDiscord: boolean | null;
}
export interface RemoveFromDiscordResult {
  guildId: string;
  outcome: "left" | "already_absent";
  stillConnectedToWorkspace: boolean;
}

export interface ValidationFinding {
  code: string;
  severity: "INFO" | "WARNING" | "ERROR" | "BLOCKER";
  title: string;
  explanation: string;
  remediation: string;
}
export interface BotInstance {
  id: string;
  guildId: string | null;
  discordApplicationId: string;
  discordBotUserId: string | null;
  configuredName: string;
  status: string;
  lastValidatedAt: string | null;
  hasCredential: boolean;
}

// ── Phase 3 types (blueprints, snapshots, dry-runs, personas) ─────────────────
// Response shapes are intentionally defensive: fields that may be absent are
// optional so the UI degrades gracefully rather than throwing.

export type Severity = "INFO" | "WARNING" | "ERROR" | "BLOCKER";
export type VersionStatus = "DRAFT" | "VALID" | "PUBLISHED" | "ARCHIVED";
export type SnapshotStatus = "REQUESTED" | "COLLECTING" | "COMPLETED" | "FAILED" | "STALE";
export type PlanStatus = "GENERATING" | "READY" | "BLOCKED" | "STALE" | "FAILED" | "SUPERSEDED";

export interface TemplateSummary {
  key: string;
  name: string;
  description: string;
}

export interface BlueprintOverwrite {
  targetResourceKey: string;
  targetType: "role" | "member";
  allow?: string[];
  deny?: string[];
}
export interface BlueprintRole {
  resourceKey: string;
  name: string;
  color?: number;
  permissions?: string[];
  hoist?: boolean;
  mentionable?: boolean;
  desiredPosition?: number;
  protected?: boolean;
  managedMode?: string;
  description?: string;
  personaTags?: string[];
}
export interface BlueprintCategory {
  resourceKey: string;
  name: string;
  desiredPosition?: number;
  protected?: boolean;
  overwrites?: BlueprintOverwrite[];
}
export interface BlueprintChannel {
  resourceKey: string;
  name: string;
  type: string;
  parentResourceKey?: string | null;
  desiredPosition?: number;
  topic?: string | null;
  nsfw?: boolean;
  protected?: boolean;
  overwrites?: BlueprintOverwrite[];
}
export interface BlueprintContent {
  schemaVersion?: string;
  metadata?: {
    name?: string;
    description?: string;
    templateType?: string;
    communityType?: string;
  };
  roles?: BlueprintRole[];
  categories?: BlueprintCategory[];
  channels?: BlueprintChannel[];
  serverSettings?: Record<string, unknown>;
  extensions?: { module: string; version: string; enabled: boolean }[];
}
export interface TemplateDetail extends TemplateSummary {
  content: BlueprintContent;
}

export interface BlueprintView {
  id: string;
  tenantId: string;
  name: string;
  description: string | null;
  communityType: string | null;
  isTemplate: boolean;
  createdAt: string;
  updatedAt: string;
}
export interface BlueprintVersion {
  id: string;
  blueprintId: string;
  version: string;
  schemaVersion?: string;
  status: VersionStatus;
  contentHash: string;
  notes: string | null;
  sourceSnapshotId?: string | null;
  validatedAt?: string | null;
  publishedAt?: string | null;
  createdAt: string;
  content: BlueprintContent | null;
}
export interface ValidationOutcome {
  valid: boolean;
  errors: { path: string; message: string }[];
  version: BlueprintVersion;
}
export interface ImportWarning {
  code: string;
  message: string;
}
export interface ImportOutcome {
  blueprint: BlueprintView;
  version: BlueprintVersion;
  mappingCount: number;
  warnings: ImportWarning[];
}

export interface SnapshotSummary {
  id: string;
  tenantId?: string;
  guildId?: string;
  status: SnapshotStatus;
  snapshotHash: string | null;
  roleCount: number;
  channelCount: number;
  categoryCount: number;
  overwriteCount: number;
  failureCode: string | null;
  failureSummary: string | null;
  requestedAt: string;
  completedAt: string | null;
}
export interface NormalizedRole {
  discordId: string;
  resourceKey: string;
  name: string;
  color: number;
  position: number;
  permissions: string;
  hoist: boolean;
  mentionable: boolean;
  managed: boolean;
  isEveryone: boolean;
  isBotManaged: boolean;
  isIntegrationManaged: boolean;
}
export interface NormalizedOverwrite {
  targetId: string;
  targetType: "role" | "member";
  targetResourceKey?: string;
  allow: string;
  deny: string;
}
export interface NormalizedChannel {
  discordId: string;
  resourceKey: string;
  name: string;
  type: string;
  parentDiscordId: string | null;
  parentResourceKey: string | null;
  position: number;
  topic: string | null;
  nsfw: boolean;
  overwrites: NormalizedOverwrite[];
  isSystem: boolean;
}
export interface NormalizedSnapshotContent {
  schemaVersion?: string;
  guild: {
    discordGuildId: string;
    name: string;
    description: string | null;
    ownerDiscordId: string;
    features?: string[];
    approximateMemberCount?: number | null;
  };
  roles: NormalizedRole[];
  channels: NormalizedChannel[];
  bot: { botUserId: string; roleIds: string[]; highestRolePosition: number };
  counts: { roles: number; categories: number; channels: number; overwrites: number };
}
export interface SnapshotDetail extends SnapshotSummary {
  content: NormalizedSnapshotContent | null;
}
/**
 * What `POST /snapshots` answers with. `coalesced` is true when the press
 * joined a collection that was already running instead of starting a second
 * one — the row is real either way, but the UI should not claim it read
 * Discord twice.
 */
export interface CreatedSnapshot extends SnapshotDetail {
  coalesced?: boolean;
}

export interface DiffOperation {
  operationId?: string;
  operationType: string;
  resourceType: "role" | "category" | "channel" | "overwrite";
  resourceKey: string;
  currentDiscordId?: string | null;
  currentState: Record<string, unknown> | null;
  desiredState: Record<string, unknown> | null;
  reason: string;
  severity: Severity;
  dependencies: string[];
  reversibility?: string;
  destructive: boolean;
  protectedResource: boolean;
  confidence: string;
}
export interface SafetyFinding {
  code: string;
  severity: Severity;
  title: string;
  plain: string;
  technical: string;
  affectedResourceKeys: string[];
  remediation: string;
  blocksDeployment: boolean;
}
export interface PersonaChannelResult {
  channelId: string;
  name: string;
  type: string;
  visible: boolean;
  canSend: boolean;
  canCreateThreads: boolean;
  canModerate: boolean;
}
export interface PersonaResult {
  personaId: string;
  label: string;
  assignedRoleIds: string[];
  guildPermissions: string[];
  isAdministrator: boolean;
  visibleChannels: string[];
  hiddenChannels: string[];
  channels: PersonaChannelResult[];
  warnings: string[];
}
export interface PersonaComparison {
  personaId: string;
  label: string;
  current: PersonaResult;
  desired: PersonaResult;
  newlyVisible: string[];
  newlyHidden: string[];
}
export interface DryRunPlan {
  schemaVersion?: string;
  status: PlanStatus;
  planHash: string;
  snapshotHash: string;
  blueprintHash: string;
  operations: DiffOperation[];
  findings: SafetyFinding[];
  cycles?: string[][];
  summary: {
    operationCounts: Record<string, number>;
    findingCounts: { info: number; warning: number; error: number; blocker: number; blocks: boolean };
  };
  personaSimulation: {
    current: PersonaResult[];
    desired: PersonaResult[];
    comparison: PersonaComparison[];
  };
}
export interface DryRunView {
  id: string;
  tenantId: string;
  guildId: string;
  blueprintVersionId: string;
  snapshotId: string;
  status: PlanStatus;
  planHash: string;
  snapshotHash: string;
  blueprintVersionHash: string;
  operationCount: number;
  blockerCount: number;
  warningCount: number;
  content: DryRunPlan;
  generatedAt: string;
}
export interface PersonaSimContent {
  current?: PersonaResult[];
  desired?: PersonaResult[];
  comparison?: PersonaComparison[];
}
export interface PersonaSimView {
  id: string;
  tenantId: string;
  guildId: string;
  mode: "current" | "desired" | "comparison" | string;
  content: PersonaSimContent;
  createdAt: string;
}

// ── Phase 4 types (controlled deployment, approval, rollback) ─────────────────
// The dashboard NEVER submits an operation list; the server derives operations
// from the immutable dry-run plan. It also NEVER holds a Discord token. Every
// shape below is intentionally token-free and secret-free by construction.
//
// Dates cross the wire as ISO strings.

export type DeploymentStatus =
  | "DRAFT"
  | "PLANNED"
  | "BLOCKED"
  | "AWAITING_APPROVAL"
  | "APPROVED"
  | "QUEUED"
  | "PREPARING"
  | "APPLYING"
  | "VERIFYING"
  | "VERIFIED"
  | "PARTIALLY_APPLIED"
  | "FAILED"
  | "ROLLBACK_AVAILABLE"
  | "ROLLBACK_QUEUED"
  | "ROLLING_BACK"
  | "ROLLED_BACK"
  | "ROLLBACK_FAILED"
  | "CANCELLED"
  | "STALE"
  | "SUPERSEDED";

export type ApprovalStatus = "PENDING" | "APPROVED" | "REJECTED" | "EXPIRED" | "CONSUMED" | "REVOKED" | "STALE";

export type VerificationResult =
  | "VERIFIED"
  | "VERIFIED_WITH_WARNINGS"
  | "PARTIALLY_APPLIED"
  | "FAILED_VERIFICATION"
  | "MANUAL_REVIEW_REQUIRED";

export type RollbackClassification = "SAFE" | "SAFE_WITH_WARNINGS" | "MANUAL_REVIEW" | "BLOCKED";

/** Safe deployment view — never contains tokens or raw Discord payloads. */
export interface DeploymentView {
  id: string;
  tenantId: string;
  guildId: string;
  blueprintVersionId: string;
  dryRunPlanId: string | null;
  status: DeploymentStatus;
  currentSnapshotHash: string | null;
  planHash: string | null;
  blueprintVersionHash: string | null;
  approvedPlanHash: string | null;
  approvedSnapshotHash: string | null;
  approvedBlueprintHash: string | null;
  safetyPolicyVersion: string;
  preSnapshotId: string | null;
  postSnapshotId: string | null;
  verificationOutcome: string | null;
  requestedByUserId: string;
  approvedByUserId: string | null;
  approvedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ApprovalView {
  id: string;
  deploymentId: string;
  tenantId: string;
  guildId: string;
  dryRunPlanId: string | null;
  requestedFromUserId: string;
  approverUserId: string | null;
  approverRole: string | null;
  status: ApprovalStatus;
  planHash: string;
  snapshotHash: string | null;
  blueprintVersionHash: string | null;
  safetyPolicyVersion: string | null;
  approvalNote: string | null;
  expiresAt: string;
  decidedAt: string | null;
  revokedAt: string | null;
  createdAt: string;
}

export interface DeploymentDecision {
  deployment: DeploymentView;
  approval: ApprovalView;
}

/** Immutable release report content (secret-free by construction on the server). */
export interface ReleaseReportContent {
  schemaVersion: string;
  reportHash: string;
  deploymentId: string;
  tenantId: string;
  guildId: string;
  blueprintVersionId: string;
  approvedPlanHash: string;
  approverUserId: string;
  approvedAt: number;
  preSnapshotHash: string;
  postSnapshotHash: string | null;
  safetyPolicyVersion: string;
  operationSummary: Record<string, number>;
  operationResults: { resourceKey: string; operationType: string; state: string; attempts: number }[];
  skipped: string[];
  failures: { resourceKey: string; operationType: string; errorCode: string; errorSummary: string }[];
  verification: { result: VerificationResult; warnings: string[] } | null;
  personaResults?: unknown;
  warnings: string[];
  rollbackAvailable: boolean;
  startedAt: number | null;
  completedAt: number | null;
  finalStatus: DeploymentStatus;
  correlationIds: string[];
}

export interface DeploymentReportView {
  id: string;
  deploymentId: string;
  schemaVersion: string;
  reportHash: string;
  finalStatus: string;
  content: ReleaseReportContent;
  createdAt: string;
}

export type RollbackActionType =
  | "delete-created-role"
  | "delete-created-category"
  | "delete-created-channel"
  | "restore-role-properties"
  | "restore-category"
  | "restore-channel"
  | "restore-role-order"
  | "restore-overwrites";

export interface RollbackAction {
  actionType: RollbackActionType;
  resourceKey: string;
  discordResourceId: string | null;
  classification: RollbackClassification;
  reason: string;
  destructive: boolean;
  requiresApproval: boolean;
}

export interface RollbackPlan {
  actions: RollbackAction[];
  classification: RollbackClassification;
  blockers: string[];
  warnings: string[];
}

export interface RollbackPlanView {
  id: string;
  deploymentId: string;
  tenantId: string;
  guildId: string;
  classification: RollbackClassification;
  planHash: string;
  content: RollbackPlan;
  createdAt: string;
}

export interface RollbackApprovalView {
  id: string;
  rollbackPlanId: string;
  approverUserId: string;
  approverRole: string;
  status: ApprovalStatus;
  planHash: string;
  expiresAt: string;
  decidedAt: string | null;
  createdAt: string;
}

export interface RollbackReportView {
  id: string;
  deploymentId: string;
  schemaVersion: string;
  reportHash: string;
  finalStatus: string;
  content: unknown;
  createdAt: string;
}

// ── Phase 5 types (BALDER AI companion) ───────────────────────────────────────
// BALDER surfaces are advisory. Tool results below are the SAFE, structured,
// already-redacted shapes the server sends — they never contain tokens or raw
// Discord payloads. Fields are optional so the UI degrades gracefully.

export type BalderConversationStatus = "ACTIVE" | "ARCHIVED";
export type BalderRole = "user" | "assistant" | "system";

export interface BalderConversation {
  id: string;
  tenantId: string;
  title: string;
  status: BalderConversationStatus;
  /** Internal ODIN guild id this conversation is scoped to, if any. */
  guildId: string | null;
  guildName?: string | null;
  /** Optional blueprint context the conversation is working against. */
  blueprintId: string | null;
  blueprintName?: string | null;
  messageCount?: number;
  createdAt: string;
  updatedAt: string;
}

export interface BalderMessage {
  id: string;
  role: BalderRole;
  content: string;
  createdAt?: string;
  /** Tool activity that accompanied an assistant turn. */
  tools?: BalderToolActivity[];
}

export interface BalderConversationDetail extends BalderConversation {
  messages: BalderMessage[];
}

// ── Tool activity (safe, structured — never raw JSON from Discord) ─────────────
export type BalderToolKind =
  | "guild-inspection"
  | "blueprint-draft-change"
  | "dry-run"
  | "deployment-review"
  | string;

export type BalderToolPhase = "proposed" | "started" | "completed" | "failed";

export interface GuildInspectionResult {
  guildName?: string | null;
  botStatus?: string | null;
  snapshotAgeSeconds?: number | null;
  snapshotFresh?: boolean;
  resourceCounts?: { roles?: number; categories?: number; channels?: number; overwrites?: number };
}
export interface BlueprintDraftChangeResult {
  blueprintId?: string | null;
  blueprintName?: string | null;
  proposedResources?: { type: string; name: string; action: string }[];
  validation?: { valid: boolean; errors?: number; warnings?: number };
  undoAvailable?: boolean;
}
export interface DryRunToolResult {
  planStatus?: PlanStatus | string;
  planHash?: string | null;
  operationCount?: number;
  warnings?: number;
  blockers?: number;
}
export interface DeploymentReviewResult {
  deploymentId?: string | null;
  planHash?: string | null;
  approvalStatus?: ApprovalStatus | string;
  productionLocked?: boolean;
}

export type BalderToolResult =
  | GuildInspectionResult
  | BlueprintDraftChangeResult
  | DryRunToolResult
  | DeploymentReviewResult
  | Record<string, unknown>;

export interface BalderToolActivity {
  /** Stable tool-call id used to merge proposed → started → completed frames. */
  id: string;
  kind: BalderToolKind;
  title?: string;
  phase: BalderToolPhase;
  summary?: string;
  /** Structured, redacted result (safe to render). */
  result?: BalderToolResult;
  error?: string;
}

// ── Confirmation (an ODIN-side action BALDER wants explicit sign-off for) ──────
export interface BalderConfirmation {
  actionId: string;
  actionType: string;
  title?: string;
  summary?: string;
  tenantId?: string;
  guildId?: string | null;
  blueprintId?: string | null;
  blueprintName?: string | null;
  /** Human-readable ODIN-side side effects (never Discord mutations). */
  sideEffects?: string[];
  /** Should be false for BALDER actions — they never touch Discord. */
  affectsDiscord?: boolean;
}

export interface BalderUsage {
  inputTokens?: number;
  outputTokens?: number;
  totalTokens?: number;
}

/**
 * The workspace's BALDER budget for the current period.
 *
 * `included` and `purchased` stay apart because they behave differently: the
 * plan allowance returns next month and a bought pack does not. `null` for
 * `included`/`allowance`/`remaining` means unmetered — it must never be rendered
 * as zero, which would read as "you have nothing left".
 */
export interface BalderAllowance {
  included: number | null;
  purchased: number;
  allowance: number | null;
  consumed: number;
  remaining: number | null;
  resetEpochSec: number | null;
  planCode: string;
  planName: string;
  /** "deny" = BALDER stops at the ceiling and nothing is charged. */
  overageBehavior: string;
}

export interface BalderActionResult {
  actionId: string;
  status: "CONFIRMED" | "REJECTED" | string;
  message?: string;
}

/** Normalized streaming events consumed from the SSE message stream. */
export type BalderStreamEvent =
  | { type: "message.started"; messageId: string | null }
  | { type: "text.delta"; delta: string }
  | { type: "tool.proposed"; tool: BalderToolActivity }
  | { type: "tool.started"; tool: BalderToolActivity }
  | { type: "tool.completed"; tool: BalderToolActivity }
  | { type: "tool.failed"; tool: BalderToolActivity }
  | { type: "confirmation.required"; confirmation: BalderConfirmation }
  | { type: "message.completed"; messageId: string | null; content: string | null; blocked: boolean; reason: string | null }
  | { type: "message.failed"; message: string }
  | { type: "usage.updated"; usage: BalderUsage }
  | { type: "allowance.updated"; allowance: BalderAllowance };

// ── SSE parsing (defensive: server field names are read leniently) ────────────
function asRecord(v: unknown): Record<string, unknown> {
  return v && typeof v === "object" ? (v as Record<string, unknown>) : {};
}

function toToolActivity(phase: BalderToolPhase, data: Record<string, unknown>): BalderToolActivity {
  const raw = asRecord(data.tool ?? data);
  return {
    id: String(raw.id ?? raw.toolCallId ?? raw.callId ?? ""),
    kind: String(raw.kind ?? raw.name ?? "tool"),
    title: raw.title != null ? String(raw.title) : undefined,
    phase,
    summary: raw.summary != null ? String(raw.summary) : undefined,
    result: (raw.result ?? raw.data) as BalderToolResult | undefined,
    error: raw.error != null ? String(raw.error) : undefined,
  };
}

const num = (v: unknown): number | null => (typeof v === "number" && Number.isFinite(v) ? v : null);

/**
 * Read an allowance off the wire without inventing numbers.
 *
 * A missing or non-numeric field becomes `null` (unknown / unmetered), never 0.
 * Rendering a failed parse as "0 requests remaining" would tell a paying
 * customer their allowance was gone, which is a lie in the alarming direction
 * and looks exactly like a billing bug.
 */
export function toBalderAllowance(raw: unknown): BalderAllowance | null {
  const a = asRecord(raw);
  if (Object.keys(a).length === 0) return null;
  const purchased = num(a.purchased);
  return {
    included: num(a.included),
    purchased: purchased ?? 0,
    allowance: num(a.allowance),
    consumed: num(a.consumed) ?? 0,
    remaining: num(a.remaining),
    resetEpochSec: num(a.resetEpochSec),
    planCode: a.planCode != null ? String(a.planCode) : "",
    planName: a.planName != null ? String(a.planName) : "",
    overageBehavior: a.overageBehavior != null ? String(a.overageBehavior) : "deny",
  };
}

/** Normalize a raw `(eventName, data)` pair into a typed stream event. */
export function normalizeBalderEvent(type: string, data: Record<string, unknown>): BalderStreamEvent | null {
  switch (type) {
    case "message.started":
      return { type, messageId: data.messageId != null ? String(data.messageId) : null };
    case "text.delta":
      return { type, delta: String(data.delta ?? data.text ?? "") };
    case "tool.proposed":
      return { type, tool: toToolActivity("proposed", data) };
    case "tool.started":
      return { type, tool: toToolActivity("started", data) };
    case "tool.completed":
      return { type, tool: toToolActivity("completed", data) };
    case "tool.failed":
      return { type, tool: toToolActivity("failed", data) };
    case "confirmation.required": {
      const c = asRecord(data.confirmation ?? data);
      return {
        type,
        confirmation: {
          actionId: String(c.actionId ?? c.id ?? ""),
          actionType: String(c.actionType ?? c.type ?? "action"),
          title: c.title != null ? String(c.title) : undefined,
          summary: c.summary != null ? String(c.summary) : undefined,
          tenantId: c.tenantId != null ? String(c.tenantId) : undefined,
          guildId: c.guildId != null ? String(c.guildId) : null,
          blueprintId: c.blueprintId != null ? String(c.blueprintId) : null,
          blueprintName: c.blueprintName != null ? String(c.blueprintName) : null,
          sideEffects: Array.isArray(c.sideEffects) ? c.sideEffects.map(String) : undefined,
          affectsDiscord: c.affectsDiscord === true,
        },
      };
    }
    case "message.completed":
      return {
        type,
        messageId: data.messageId != null ? String(data.messageId) : null,
        content: data.content != null ? String(data.content) : null,
        // A blocked turn still completes — the text already streamed is the
        // explanation, not a partial answer, and the reason says which limit
        // stopped it (allowance, rate limit, secret guard).
        blocked: data.blocked === true,
        reason: data.reason != null ? String(data.reason) : null,
      };
    case "message.failed":
      return { type, message: String(data.message ?? asRecord(data.error).message ?? "BALDER could not complete this response.") };
    case "usage.updated": {
      const u = asRecord(data.usage ?? data);
      return {
        type,
        usage: {
          inputTokens: typeof u.inputTokens === "number" ? u.inputTokens : undefined,
          outputTokens: typeof u.outputTokens === "number" ? u.outputTokens : undefined,
          totalTokens: typeof u.totalTokens === "number" ? u.totalTokens : undefined,
        },
      };
    }
    case "allowance.updated": {
      const allowance = toBalderAllowance(data.allowance ?? data);
      return allowance ? { type, allowance } : null;
    }
    default:
      return null;
  }
}

/** Parse one SSE frame (a block separated by a blank line). */
export function parseBalderSseFrame(frame: string): BalderStreamEvent | null {
  let eventName = "";
  const dataLines: string[] = [];
  for (const rawLine of frame.split("\n")) {
    const line = rawLine.replace(/\r$/, "");
    if (line.startsWith(":")) continue; // heartbeat / comment
    if (line.startsWith("event:")) eventName = line.slice(6).trim();
    else if (line.startsWith("data:")) dataLines.push(line.slice(5).replace(/^ /, ""));
  }
  const dataRaw = dataLines.join("\n");
  if (!eventName && !dataRaw) return null;
  let data: Record<string, unknown> = {};
  if (dataRaw && dataRaw !== "[DONE]") {
    try {
      data = asRecord(JSON.parse(dataRaw));
    } catch {
      data = { raw: dataRaw };
    }
  }
  const type = (eventName || String(data.type ?? "")).trim();
  return normalizeBalderEvent(type, data);
}

/**
 * Send a message to BALDER and consume the SSE reply.
 *
 * The body field is `message`. The server reads `body.message` and answers
 * `400 invalid_request` to anything else — this client previously sent
 * `{ content }`, so every message a customer typed was rejected before it ever
 * reached the model. Keep this in step with `routes-balder.ts`; the wire shape
 * is pinned by a test for that reason.
 */
async function streamBalderMessage(
  tenantId: string,
  conversationId: string,
  input: { message: string },
  onEvent: (event: BalderStreamEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  const res = await fetch(
    `${API_BASE}/api/v1/tenants/${tenantId}/balder/conversations/${conversationId}/messages`,
    {
      method: "POST",
      credentials: "include",
      headers: { accept: "text/event-stream", "content-type": "application/json" },
      body: JSON.stringify(input),
      signal,
    },
  );
  if (!res.ok || !res.body) {
    const body = await res.json().catch(() => ({}));
    const err = (body as { error?: { code?: string; message?: string } }).error;
    throw new ApiError(res.status, err?.code ?? "error", err?.message ?? `Request failed (${res.status})`);
  }
  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let idx: number;
    while ((idx = buffer.indexOf("\n\n")) !== -1) {
      const frame = buffer.slice(0, idx);
      buffer = buffer.slice(idx + 2);
      const event = parseBalderSseFrame(frame);
      if (event) onEvent(event);
    }
  }
  const tail = parseBalderSseFrame(buffer);
  if (tail) onEvent(tail);
}

const balder = {
  listConversations: (tenantId: string) =>
    request<{ conversations: BalderConversation[] }>(`/api/v1/tenants/${tenantId}/balder/conversations`),
  /**
   * The workspace's BALDER budget. Readable by any member — the person typing
   * into BALDER is usually not the person who can open the billing page, and
   * they are the one the limit applies to. Carries no price, invoice or card.
   */
  getUsage: (tenantId: string) =>
    request<{ allowance: BalderAllowance | null; metered: boolean }>(`/api/v1/tenants/${tenantId}/balder/usage`),
  createConversation: (tenantId: string, input: { title?: string; guildId?: string | null; blueprintId?: string | null }) =>
    request<BalderConversation>(`/api/v1/tenants/${tenantId}/balder/conversations`, {
      method: "POST",
      body: JSON.stringify(input),
    }),
  getConversation: (tenantId: string, conversationId: string) =>
    request<BalderConversationDetail>(`/api/v1/tenants/${tenantId}/balder/conversations/${conversationId}`),
  renameConversation: (tenantId: string, conversationId: string, title: string) =>
    request<BalderConversation>(`/api/v1/tenants/${tenantId}/balder/conversations/${conversationId}`, {
      method: "PATCH",
      body: JSON.stringify({ title }),
    }),
  archiveConversation: (tenantId: string, conversationId: string) =>
    request<void>(`/api/v1/tenants/${tenantId}/balder/conversations/${conversationId}`, { method: "DELETE" }),
  cancel: (tenantId: string, conversationId: string) =>
    request<{ status: string }>(`/api/v1/tenants/${tenantId}/balder/conversations/${conversationId}/cancel`, {
      method: "POST",
    }),
  // Confirm / reject an ODIN-side action BALDER proposed. These never deploy to,
  // approve for, or otherwise mutate a live Discord server.
  confirmAction: (tenantId: string, actionId: string) =>
    request<BalderActionResult>(`/api/v1/tenants/${tenantId}/balder/actions/${actionId}/confirm`, { method: "POST" }),
  rejectAction: (tenantId: string, actionId: string) =>
    request<BalderActionResult>(`/api/v1/tenants/${tenantId}/balder/actions/${actionId}/reject`, { method: "POST" }),
  /** Send a message and consume the SSE response, invoking `onEvent` per frame. */
  streamMessage: streamBalderMessage,
};

// ── Phase 6A types (community onboarding & verification) ──────────────────────
// Everything below is a RECORD/CONFIG surface. None of it mutates Discord: a
// draft is a draft, a dry-run is a preview, and activation still routes through
// the EXISTING Phase 4 approval + deployment pipeline. No Discord role id is ever
// sent from the browser — role effects run server-side behind a guard. Dates
// cross the wire as ISO strings.

export const ONBOARDING_MODES = [
  "INFORMATION_ONLY",
  "RULES_ACKNOWLEDGEMENT",
  "BUTTON_VERIFICATION",
  "MANUAL_APPROVAL",
  "HYBRID",
] as const;
export type OnboardingMode = (typeof ONBOARDING_MODES)[number];

export const ROLE_MENU_TYPES = ["SINGLE_SELECT", "MULTI_SELECT", "TOGGLE_BUTTONS", "EXCLUSIVE_GROUP"] as const;
export type RoleMenuType = (typeof ROLE_MENU_TYPES)[number];

export type ManagedMessageType = "WELCOME" | "RULES" | "VERIFICATION" | "ROLE_MENU" | "ONBOARDING_HELP";
export type ManagedMessageStatus = "PLANNED" | "ACTIVE" | "DEGRADED" | "MISSING" | "OUTDATED" | "REVOKED";
export type OnboardingFindingSeverity = "INFO" | "WARNING" | "BLOCKER";
export type VerificationStatus =
  | "PENDING"
  | "VERIFIED"
  | "MANUAL_REVIEW"
  | "APPROVED"
  | "REJECTED"
  | "EXPIRED"
  | "FAILED";

/** Structured, safe message content — never a raw Discord payload. */
export interface OnboardingAllowedMentions {
  everyone: boolean;
  roles: string[];
  users: boolean;
}
export interface OnboardingMessageField {
  name: string;
  value: string;
}
export interface OnboardingMessageContent {
  plainText?: string;
  title?: string;
  description?: string;
  fields?: OnboardingMessageField[];
  footer?: string;
  imageUrl?: string;
  thumbnailUrl?: string;
  allowedMentions?: OnboardingAllowedMentions;
}
export type OnboardingMessageSlot =
  | "welcome"
  | "rulesAck"
  | "verificationPrompt"
  | "verificationSuccess"
  | "verificationFailure";

export interface OnboardingBehavior {
  requireAcknowledgement?: boolean;
  reAcknowledgeOnChange?: boolean;
  dmWelcome?: boolean;
  assignRoleOnAcknowledge?: boolean;
  manualReviewExpiryHours?: number;
}

/** The onboarding module — the draft the wizard builds and submits. */
export interface OnboardingModuleContent {
  enabled: boolean;
  schemaVersion: "1";
  mode: OnboardingMode;
  welcomeChannelKey?: string;
  rulesChannelKey?: string;
  verificationChannelKey?: string;
  verifiedRoleKey?: string;
  unverifiedRoleKey?: string;
  messages?: Partial<Record<OnboardingMessageSlot, OnboardingMessageContent>>;
  behavior?: OnboardingBehavior;
}

export interface OnboardingConfigView {
  id: string;
  tenantId: string;
  guildId: string;
  enabled: boolean;
  activeVersionId: string | null;
  createdAt: string;
  updatedAt: string;
}
export interface OnboardingVersionView {
  id: string;
  configurationId: string;
  tenantId: string;
  guildId: string;
  version: number;
  mode: OnboardingMode;
  schemaVersion: string;
  blueprintVersionId: string | null;
  deploymentId: string | null;
  content: OnboardingModuleContent;
  contentHash: string;
  createdAt: string;
}
export interface OnboardingConfigResponse {
  configuration: OnboardingConfigView | null;
  versions: OnboardingVersionView[];
}
export interface OnboardingValidationOutcome {
  valid: boolean;
  errors: { path: string; message: string }[];
}
export interface OnboardingActivateResult {
  configuration: OnboardingConfigView;
  version: OnboardingVersionView;
}

export interface ManagedMessageView {
  id: string;
  tenantId: string;
  guildId: string;
  discordChannelId: string;
  discordMessageId: string | null;
  messageType: ManagedMessageType;
  configVersion: number;
  contentHash: string;
  componentHash: string;
  status: ManagedMessageStatus;
  lastVerifiedAt: string | null;
}
/** Shape returned by GET …/status (all real, server-computed record values). */
export interface OnboardingStatusView {
  enabled: boolean;
  activeVersionId: string | null;
  versionCount: number;
  managedMessages: ManagedMessageView[];
}

export interface RoleMenuOptionContent {
  roleKey: string;
  label: string;
  description?: string;
  emoji?: string;
  removable?: boolean;
  exclusiveGroup?: string;
}
export interface RoleMenuContent {
  id: string;
  title: string;
  description?: string;
  channelKey: string;
  menuType: RoleMenuType;
  minSelections?: number;
  maxSelections?: number;
  requireVerified?: boolean;
  enabled?: boolean;
  version?: string;
  options: RoleMenuOptionContent[];
}
export interface RoleMenuView {
  id: string;
  tenantId: string;
  guildId: string;
  menuKey: string;
  title: string;
  channelKey: string;
  menuType: RoleMenuType;
  requireVerified: boolean;
  enabled: boolean;
  activeVersionId: string | null;
  createdAt: string;
  updatedAt: string;
}
export interface RoleMenuVersionView {
  id: string;
  roleMenuId: string;
  version: number;
  content: RoleMenuContent;
  contentHash: string;
  createdAt: string;
}
export interface RoleMenuFinding {
  code: string;
  severity: OnboardingFindingSeverity;
  menuId: string;
  roleKey?: string;
  message: string;
}
export interface RoleMenuValidation {
  findings: RoleMenuFinding[];
  summary: { blocked: boolean; blockers: number; warnings: number };
}

export type OnboardingOperationType =
  | "upsert-managed-message"
  | "register-role-menu-message"
  | "associate-channel-function"
  | "associate-role-function"
  | "register-application-commands";
export interface OnboardingOperation {
  operationId: string;
  operationType: OnboardingOperationType;
  messageType?: ManagedMessageType;
  channelKey?: string;
  roleKey?: string;
  menuId?: string;
  contentHash?: string;
  reason: string;
}
/** Onboarding dry-run preview. Pure planning — nothing is created or applied. */
export interface OnboardingPlanPreview {
  blueprintVersionId: string;
  operations: OnboardingOperation[];
  findings: RoleMenuFinding[];
  summary: { blocked: boolean; blockers: number; warnings: number };
  blocked: boolean;
}

/** A manual-review request (verification record). */
export interface OnboardingReviewView {
  id: string;
  tenantId: string;
  guildId: string;
  discordUserId: string;
  configVersion: number;
  status: VerificationStatus;
  managedMessageId: string | null;
  interactionRef: string | null;
  idempotencyKey: string;
  failureCode: string | null;
  expiresAt: string | null;
  createdAt: string;
}

/**
 * Wrap an onboarding module into a minimal blueprint content so the server's
 * blueprint validator can resolve its role/channel references. Mirrors the
 * server's own `wrapModuleInBlueprint` — the referenced keys are declared so
 * they resolve, and the strict schema still rejects malformed module config.
 */
export function buildOnboardingContent(module: OnboardingModuleContent): unknown {
  const roleKeys = new Set<string>();
  const channelKeys = new Set<string>();
  for (const k of [module.verifiedRoleKey, module.unverifiedRoleKey]) if (k) roleKeys.add(k);
  for (const k of [module.welcomeChannelKey, module.rulesChannelKey, module.verificationChannelKey]) if (k) channelKeys.add(k);
  return {
    schemaVersion: "1",
    metadata: { name: "onboarding-module", templateType: "custom" },
    roles: [...roleKeys].map((resourceKey, i) => ({ resourceKey, name: resourceKey.slice(0, 90) || `r${i}`, desiredPosition: i + 1 })),
    categories: [],
    channels: [...channelKeys].map((resourceKey, i) => ({ resourceKey, name: resourceKey.slice(0, 90) || `c${i}`, type: "text", desiredPosition: i })),
    modules: { onboarding: module },
  };
}

/** Wrap a single role menu into a minimal blueprint content for submission. */
export function buildRoleMenuContent(menu: RoleMenuContent): unknown {
  const roleKeys = new Set<string>(menu.options.map((o) => o.roleKey));
  const channelKeys = new Set<string>([menu.channelKey]);
  return {
    schemaVersion: "1",
    metadata: { name: "role-menu-module", templateType: "custom" },
    roles: [...roleKeys].map((resourceKey, i) => ({ resourceKey, name: resourceKey.slice(0, 90) || `r${i}`, desiredPosition: i + 1 })),
    categories: [],
    channels: [...channelKeys].map((resourceKey, i) => ({ resourceKey, name: resourceKey.slice(0, 90) || `c${i}`, type: "text", desiredPosition: i })),
    modules: { roleMenus: { enabled: true, schemaVersion: "1", menus: [menu] } },
  };
}

// All onboarding routes are nested under a tenant + ODIN guild id. The client
// never posts a Discord token or a raw Discord role/channel id here.
const onboarding = {
  path: (tenantId: string, guildId: string) => `/api/v1/tenants/${tenantId}/guilds/${guildId}/onboarding`,
  getConfig: (tenantId: string, guildId: string) =>
    request<OnboardingConfigResponse>(`${onboarding.path(tenantId, guildId)}/config`),
  getVersion: (tenantId: string, guildId: string, versionId: string) =>
    request<OnboardingVersionView>(`${onboarding.path(tenantId, guildId)}/config/versions/${versionId}`),
  createDraftVersion: (tenantId: string, guildId: string, content: unknown) =>
    request<OnboardingVersionView>(`${onboarding.path(tenantId, guildId)}/config/versions`, {
      method: "POST",
      body: JSON.stringify({ content }),
    }),
  validateVersion: (tenantId: string, guildId: string, versionId: string) =>
    request<OnboardingValidationOutcome>(`${onboarding.path(tenantId, guildId)}/config/versions/${versionId}/validate`, {
      method: "POST",
    }),
  /**
   * Link a validated draft to a blueprint version + Phase 4 deployment and mark it
   * active. This does NOT deploy or approve anything itself — the deployment id it
   * links must already have travelled the Phase 4 dry-run → approval → queue path.
   */
  activateVersion: (
    tenantId: string,
    guildId: string,
    versionId: string,
    input: { blueprintVersionId?: string | null; deploymentId?: string | null } = {},
  ) =>
    request<OnboardingActivateResult>(`${onboarding.path(tenantId, guildId)}/config/versions/${versionId}/activate`, {
      method: "POST",
      body: JSON.stringify({ blueprintVersionId: input.blueprintVersionId ?? null, deploymentId: input.deploymentId ?? null }),
    }),
  getStatus: (tenantId: string, guildId: string) =>
    request<OnboardingStatusView>(`${onboarding.path(tenantId, guildId)}/status`),
  listManagedMessages: (tenantId: string, guildId: string) =>
    request<{ messages: ManagedMessageView[] }>(`${onboarding.path(tenantId, guildId)}/managed-messages`),
  listRoleMenus: (tenantId: string, guildId: string) =>
    request<{ menus: RoleMenuView[] }>(`${onboarding.path(tenantId, guildId)}/role-menus`),
  createRoleMenu: (tenantId: string, guildId: string, input: { menuKey: string; content: unknown }) =>
    request<RoleMenuVersionView>(`${onboarding.path(tenantId, guildId)}/role-menus`, {
      method: "POST",
      body: JSON.stringify(input),
    }),
  updateRoleMenu: (tenantId: string, guildId: string, menuKey: string, content: unknown) =>
    request<RoleMenuVersionView>(`${onboarding.path(tenantId, guildId)}/role-menus/${menuKey}`, {
      method: "PUT",
      body: JSON.stringify({ content }),
    }),
  validateRoleMenu: (tenantId: string, guildId: string, menuKey: string) =>
    request<RoleMenuValidation>(`${onboarding.path(tenantId, guildId)}/role-menus/${menuKey}/validate`),
  dryRun: (tenantId: string, guildId: string, blueprintVersionId: string) =>
    request<OnboardingPlanPreview>(`${onboarding.path(tenantId, guildId)}/dry-run`, {
      method: "POST",
      body: JSON.stringify({ blueprintVersionId }),
    }),
  getReview: (tenantId: string, guildId: string, requestId: string) =>
    request<OnboardingReviewView>(`${onboarding.path(tenantId, guildId)}/reviews/${requestId}`),
  approveReview: (tenantId: string, guildId: string, requestId: string, note?: string | null) =>
    request<OnboardingReviewView>(`${onboarding.path(tenantId, guildId)}/reviews/${requestId}/approve`, {
      method: "POST",
      body: JSON.stringify({ note: note ?? null }),
    }),
  rejectReview: (tenantId: string, guildId: string, requestId: string, note?: string | null) =>
    request<OnboardingReviewView>(`${onboarding.path(tenantId, guildId)}/reviews/${requestId}/reject`, {
      method: "POST",
      body: JSON.stringify({ note: note ?? null }),
    }),
};

// ── Phase 6B types (moderation, automod, logging) ─────────────────────────────
// SAFETY: this whole surface is a RECORD/CONFIG surface. A moderation action is
// requested through the server's LOCKED mutation boundary — the control-api never
// talks to Discord, and no live effect happens until the platform is deliberately
// unlocked (records QUEUED). The browser only ever sends the target's Discord user
// id (re-resolved server-side); it never sends the actor role, actor permissions,
// or any target facts. Automod drafts / previews and logging drafts never touch
// Discord; activation still routes through the EXISTING Phase 4 approval pipeline.
// No Discord token is ever stored or sent. Dates cross the wire as ISO strings.

export const MODERATION_ACTIONS = ["WARN", "TIMEOUT", "UNTIMEOUT", "KICK", "BAN", "UNBAN", "NOTE"] as const;
export type ModerationActionType = (typeof MODERATION_ACTIONS)[number];

/** Enforcement actions require a reason; NOTE / reversals do not. */
export const ENFORCEMENT_ACTIONS: readonly ModerationActionType[] = ["WARN", "TIMEOUT", "KICK", "BAN"];

export const MODERATION_CASE_STATUSES = ["OPEN", "ACTIONED", "APPEALED", "OVERTURNED", "EXPIRED", "CLOSED"] as const;
export type ModerationCaseStatus = (typeof MODERATION_CASE_STATUSES)[number];

export type ModerationActionResult = "QUEUED" | "APPLIED" | "FAILED" | "BLOCKED" | "PENDING";

export interface EscalationRecommendation {
  atWarnings: number;
  recommend: "TIMEOUT" | "KICK" | "BAN";
  timeoutSeconds?: number;
}

export interface ModerationCase {
  id: string;
  tenantId: string;
  guildId: string;
  caseNumber: number | null;
  subjectDiscordUserId: string;
  actorDiscordUserId: string;
  actionType: string;
  status: ModerationCaseStatus;
  reason: string | null;
  durationSeconds: number | null;
  active: boolean;
  expiresAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ModerationActionRecord {
  id: string;
  tenantId: string;
  guildId: string;
  caseId: string | null;
  actorUserId: string | null;
  actorDiscordUserId: string | null;
  subjectDiscordUserId: string;
  actionType: string;
  reason: string | null;
  durationSeconds: number | null;
  result: string;
  failureCode: string | null;
  correlationId: string | null;
  idempotencyKey: string;
  createdAt: string;
}

export interface ModerationCaseDetail {
  case: ModerationCase;
  actions: ModerationActionRecord[];
}

export interface TakeActionInput {
  action: ModerationActionType;
  subjectDiscordUserId: string;
  reason?: string | null;
  durationSeconds?: number | null;
}

export interface TakeActionResult {
  action: { id: string; actionType: string; result: string; caseId: string | null };
  case: { id: string; status: ModerationCaseStatus };
  warning: { id: string } | null;
  activeWarnings: number;
  escalation: EscalationRecommendation | null;
}

export interface MemberWarning {
  id: string;
  tenantId: string;
  guildId: string;
  subjectDiscordUserId: string;
  actorUserId: string | null;
  reason: string;
  active: boolean;
  caseId: string | null;
  createdAt: string;
  expiresAt: string | null;
}

export interface WarningSummary {
  subjectDiscordUserId: string;
  activeWarnings: number;
  warnings: MemberWarning[];
  escalation: EscalationRecommendation | null;
}

// ── Automod (structured rule config — never raw JSON on the wire) ─────────────
export const AUTOMOD_RULE_TYPES = ["keyword", "invite-link", "mass-mention", "repeated-text", "link-domain"] as const;
export type AutomodRuleType = (typeof AUTOMOD_RULE_TYPES)[number];

export const AUTOMOD_ACTIONS = ["flag", "delete", "warn", "timeout"] as const;
export type AutomodAction = (typeof AUTOMOD_ACTIONS)[number];

export interface AutomodRule {
  id: string;
  type: AutomodRuleType;
  enabled: boolean;
  action: AutomodAction;
  terms: string[];
  maxMentions?: number;
  maxRepeats?: number;
  exemptRoleKeys: string[];
  timeoutSeconds?: number;
}

export interface AutomodModule {
  enabled: boolean;
  schemaVersion: "1";
  rules: AutomodRule[];
}

export interface AutomodConfigView {
  id: string;
  tenantId: string;
  guildId: string;
  enabled: boolean;
  activeVersionId: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface AutomodVersionView {
  id: string;
  configurationId: string;
  tenantId: string;
  guildId: string;
  version: number;
  schemaVersion: string;
  blueprintVersionId: string | null;
  deploymentId: string | null;
  content: AutomodModule;
  contentHash: string;
  createdAt: string;
}

export interface AutomodConfigResponse {
  configuration: AutomodConfigView | null;
  versions: AutomodVersionView[];
}

export interface ModerationValidationOutcome {
  valid: boolean;
  errors: { path: string; message: string }[];
}

export interface AutomodActivateResult {
  configuration: AutomodConfigView;
  version: AutomodVersionView;
}

export interface AutomodViolation {
  ruleId: string;
  type: AutomodRuleType;
  action: string;
  reason: string;
}

export interface AutomodPreviewSample {
  content: string;
  mentionCount?: number;
  authorRoleKeys?: string[];
  recentIdenticalCount?: number;
}

export interface AutomodPreviewResult {
  violations: AutomodViolation[];
  ruleCount: number;
  matched: boolean;
}

// ── Operational logging ──────────────────────────────────────────────────────
export const LOG_EVENT_CATEGORIES = [
  "moderation",
  "member-join",
  "member-leave",
  "role-change",
  "channel-change",
  "onboarding",
  "automod",
  "deployment",
] as const;
export type LogEventCategory = (typeof LOG_EVENT_CATEGORIES)[number];

export interface LoggingModule {
  enabled: boolean;
  schemaVersion: "1";
  logChannelKey?: string;
  categories: LogEventCategory[];
  redactContent: boolean;
}

export interface LoggingConfigView {
  id: string;
  tenantId: string;
  guildId: string;
  enabled: boolean;
  logChannelKey: string | null;
  categories: LogEventCategory[];
  redactContent: boolean;
  activeVersionId: string | null;
  blueprintVersionId: string | null;
  deploymentId: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * Wrap an automod module into a minimal blueprint content so the server's
 * blueprint validator can resolve its exempt-role references. Mirrors the
 * server's own `wrapAutomodInBlueprint`: referenced role keys are declared so
 * they resolve, and the strict schema still rejects malformed rule config.
 */
export function buildAutomodContent(module: AutomodModule): unknown {
  const roleKeys = new Set<string>();
  for (const rule of module.rules) for (const k of rule.exemptRoleKeys) roleKeys.add(k);
  return {
    schemaVersion: "1",
    metadata: { name: "automod-module", templateType: "custom" },
    roles: [...roleKeys].map((resourceKey, i) => ({ resourceKey, name: resourceKey.slice(0, 90) || `r${i}`, desiredPosition: i + 1 })),
    categories: [],
    channels: [],
    modules: { automod: module },
  };
}

/** Wrap a logging module into a minimal blueprint content for submission. */
export function buildLoggingContent(module: LoggingModule): unknown {
  const channelKeys = new Set<string>();
  if (module.logChannelKey) channelKeys.add(module.logChannelKey);
  return {
    schemaVersion: "1",
    metadata: { name: "logging-module", templateType: "custom" },
    roles: [],
    categories: [],
    channels: [...channelKeys].map((resourceKey, i) => ({ resourceKey, name: resourceKey.slice(0, 90) || `c${i}`, type: "text", desiredPosition: i })),
    modules: { logging: module },
  };
}

// All moderation routes are nested under a tenant + ODIN guild id. The client
// never posts a Discord token or an actor/target fact — only the subject's
// Discord user id, which the server re-resolves behind the guarded, LOCKED path.
const moderation = {
  path: (tenantId: string, guildId: string) => `/api/v1/tenants/${tenantId}/guilds/${guildId}/moderation`,

  // ── Moderation actions + case lifecycle ─────────────────────────────────────
  takeAction: (tenantId: string, guildId: string, input: TakeActionInput) =>
    request<TakeActionResult>(`${moderation.path(tenantId, guildId)}/actions`, {
      method: "POST",
      body: JSON.stringify({
        action: input.action,
        subjectDiscordUserId: input.subjectDiscordUserId,
        reason: input.reason ?? null,
        durationSeconds: input.durationSeconds ?? null,
      }),
    }),
  listCases: (tenantId: string, guildId: string, filter: { subjectDiscordUserId?: string; status?: ModerationCaseStatus; limit?: number } = {}) => {
    const q = new URLSearchParams();
    if (filter.subjectDiscordUserId) q.set("subjectDiscordUserId", filter.subjectDiscordUserId);
    if (filter.status) q.set("status", filter.status);
    if (filter.limit) q.set("limit", String(filter.limit));
    const qs = q.toString();
    return request<{ cases: ModerationCase[] }>(`${moderation.path(tenantId, guildId)}/cases${qs ? `?${qs}` : ""}`);
  },
  getCase: (tenantId: string, guildId: string, caseId: string) =>
    request<ModerationCaseDetail>(`${moderation.path(tenantId, guildId)}/cases/${caseId}`),
  appealCase: (tenantId: string, guildId: string, caseId: string) =>
    request<{ case: { id: string; status: ModerationCaseStatus } }>(`${moderation.path(tenantId, guildId)}/cases/${caseId}/appeal`, { method: "POST" }),
  overturnCase: (tenantId: string, guildId: string, caseId: string) =>
    request<{ case: { id: string; status: ModerationCaseStatus } }>(`${moderation.path(tenantId, guildId)}/cases/${caseId}/overturn`, { method: "POST" }),
  closeCase: (tenantId: string, guildId: string, caseId: string) =>
    request<{ case: { id: string; status: ModerationCaseStatus } }>(`${moderation.path(tenantId, guildId)}/cases/${caseId}/close`, { method: "POST" }),

  // ── Member warnings (read + record-only; escalation is advisory) ────────────
  getWarnings: (tenantId: string, guildId: string, subjectDiscordUserId: string) =>
    request<WarningSummary>(`${moderation.path(tenantId, guildId)}/members/${subjectDiscordUserId}/warnings`),
  expireWarning: (tenantId: string, guildId: string, warningId: string) =>
    request<{ warning: { id: string; active: boolean } }>(`${moderation.path(tenantId, guildId)}/warnings/${warningId}/expire`, { method: "POST" }),

  // ── Automod configuration (versioned; drafts + preview never touch Discord) ──
  getAutomodConfig: (tenantId: string, guildId: string) =>
    request<AutomodConfigResponse>(`${moderation.path(tenantId, guildId)}/automod/config`),
  getAutomodVersion: (tenantId: string, guildId: string, versionId: string) =>
    request<AutomodVersionView>(`${moderation.path(tenantId, guildId)}/automod/config/versions/${versionId}`),
  createAutomodDraft: (tenantId: string, guildId: string, content: unknown) =>
    request<AutomodVersionView>(`${moderation.path(tenantId, guildId)}/automod/config/versions`, {
      method: "POST",
      body: JSON.stringify({ content }),
    }),
  validateAutomodVersion: (tenantId: string, guildId: string, versionId: string) =>
    request<ModerationValidationOutcome>(`${moderation.path(tenantId, guildId)}/automod/config/versions/${versionId}/validate`, { method: "POST" }),
  /**
   * Link a validated draft to a blueprint version + Phase 4 deployment and mark it
   * active. This does NOT deploy or approve anything itself — the deployment id it
   * links must already have travelled the Phase 4 dry-run → approval → queue path.
   */
  activateAutomodVersion: (
    tenantId: string,
    guildId: string,
    versionId: string,
    input: { blueprintVersionId?: string | null; deploymentId?: string | null } = {},
  ) =>
    request<AutomodActivateResult>(`${moderation.path(tenantId, guildId)}/automod/config/versions/${versionId}/activate`, {
      method: "POST",
      body: JSON.stringify({ blueprintVersionId: input.blueprintVersionId ?? null, deploymentId: input.deploymentId ?? null }),
    }),
  /** Read-only rule test over a sample message. Never scans live Discord content. */
  previewAutomod: (
    tenantId: string,
    guildId: string,
    input: { versionId?: string; content?: unknown; sample: AutomodPreviewSample },
  ) =>
    request<AutomodPreviewResult>(`${moderation.path(tenantId, guildId)}/automod/preview`, {
      method: "POST",
      body: JSON.stringify({ versionId: input.versionId, content: input.content, sample: input.sample }),
    }),

  // ── Operational logging configuration ───────────────────────────────────────
  getLoggingConfig: (tenantId: string, guildId: string) =>
    request<{ configuration: LoggingConfigView | null }>(`${moderation.path(tenantId, guildId)}/logging/config`),
  saveLoggingDraft: (tenantId: string, guildId: string, content: unknown) =>
    request<LoggingConfigView>(`${moderation.path(tenantId, guildId)}/logging/config`, {
      method: "PUT",
      body: JSON.stringify({ content }),
    }),
  validateLoggingDraft: (tenantId: string, guildId: string, content: unknown) =>
    request<ModerationValidationOutcome>(`${moderation.path(tenantId, guildId)}/logging/config/validate`, {
      method: "POST",
      body: JSON.stringify({ content }),
    }),
  activateLogging: (
    tenantId: string,
    guildId: string,
    input: { blueprintVersionId?: string | null; deploymentId?: string | null } = {},
  ) =>
    request<LoggingConfigView>(`${moderation.path(tenantId, guildId)}/logging/config/activate`, {
      method: "POST",
      body: JSON.stringify({ blueprintVersionId: input.blueprintVersionId ?? null, deploymentId: input.deploymentId ?? null }),
    }),
};

// ── Phase 6C types (engagement: tickets, suggestions, polls, scheduled content) ─
// SAFETY: this whole surface is a RECORD/CONFIG surface. Nothing here mutates
// Discord directly. Ticket channel creation and vote / announcement message
// posting are requested through the LOCKED engagement boundary (records QUEUED,
// no live Discord effect by default). Engagement config drafts are versioned;
// validation never touches Discord and activation still binds to a deployment
// that already cleared the Phase 4 approval pipeline. Every count returned is a
// real DB value — none is fabricated. No Discord token ever leaves the browser.
// Dates cross the wire as ISO strings.

export const ENGAGEMENT_SCHEMA_VERSION = "1" as const;

export interface TicketsModule {
  enabled: boolean;
  schemaVersion: "1";
  panelChannelKey?: string;
  ticketCategoryKey?: string;
  supportRoleKeys: string[];
  maxOpenPerUser: number;
  cooldownSeconds: number;
}
export interface SuggestionsModule {
  enabled: boolean;
  schemaVersion: "1";
  channelKey?: string;
  allowDownvotes: boolean;
  reviewRoleKeys: string[];
}
export interface PollsModule {
  enabled: boolean;
  schemaVersion: "1";
  channelKey?: string;
  allowMultipleChoice: boolean;
}
export interface EventsModule {
  enabled: boolean;
  schemaVersion: "1";
  announceChannelKey?: string;
  allowRsvp: boolean;
}
export interface StatisticsModule {
  enabled: boolean;
  schemaVersion: "1";
  memberCountChannelKey?: string;
  refreshIntervalSeconds: number;
}

/** The small, safe schedule union ODIN supports — never arbitrary cron. */
export type EngagementSchedule =
  | { kind: "one-shot"; atEpochSec: number }
  | { kind: "interval"; everySec: number; anchorEpochSec: number }
  | { kind: "daily"; secondOfDayUtc: number }
  | { kind: "weekly"; dayOfWeekUtc: number; secondOfDayUtc: number };

export const SCHEDULE_KINDS = ["one-shot", "interval", "daily", "weekly"] as const;
export type ScheduleKind = (typeof SCHEDULE_KINDS)[number];

/** Structured announcement message content — never a raw Discord payload. */
export interface EngagementMessageContent {
  plainText?: string;
  title?: string;
  description?: string;
}
export interface AnnouncementItem {
  id: string;
  channelKey: string;
  message: EngagementMessageContent;
  schedule: EngagementSchedule;
  enabled: boolean;
}
export interface AnnouncementsModule {
  enabled: boolean;
  schemaVersion: "1";
  items: AnnouncementItem[];
}

/** The engagement modules bundle — the draft the config builder submits. */
export interface EngagementModules {
  tickets?: TicketsModule;
  suggestions?: SuggestionsModule;
  polls?: PollsModule;
  announcements?: AnnouncementsModule;
  events?: EventsModule;
  statistics?: StatisticsModule;
}

export interface EngagementConfigView {
  id: string;
  tenantId: string;
  guildId: string;
  enabled: boolean;
  activeVersionId: string | null;
  createdAt: string;
  updatedAt: string;
}
export interface EngagementVersionView {
  id: string;
  configurationId: string;
  tenantId: string;
  guildId: string;
  version: number;
  schemaVersion: string;
  blueprintVersionId: string | null;
  deploymentId: string | null;
  content: EngagementModules;
  contentHash: string;
  createdAt: string;
}
export interface EngagementConfigResponse {
  configuration: EngagementConfigView | null;
  versions: EngagementVersionView[];
}
export interface EngagementValidationOutcome {
  valid: boolean;
  errors: { path: string; message: string }[];
}
export interface EngagementActivateResult {
  configuration: EngagementConfigView;
  version: EngagementVersionView;
}

// ── Tickets ───────────────────────────────────────────────────────────────────
export const TICKET_STATUSES = ["OPEN", "CLAIMED", "AWAITING_USER", "RESOLVED", "CLOSED", "ARCHIVED"] as const;
export type TicketStatus = (typeof TICKET_STATUSES)[number];

export interface TicketRow {
  id: string;
  tenantId: string;
  guildId: string;
  discordChannelId: string | null;
  requesterDiscordUserId: string;
  category: string;
  subject: string | null;
  status: TicketStatus;
  assignedDiscordUserId: string | null;
  claimedByUserId: string | null;
  closedByUserId: string | null;
  lastActivityAt: string | null;
  openedAt: string;
  closedAt: string | null;
}

// ── Suggestions ───────────────────────────────────────────────────────────────
export const SUGGESTION_STATUSES = ["OPEN", "APPROVED", "REJECTED", "IMPLEMENTED", "CLOSED"] as const;
export type SuggestionStatus = (typeof SUGGESTION_STATUSES)[number];
export const SUGGESTION_CLOSE_STATUSES: readonly SuggestionStatus[] = ["APPROVED", "REJECTED", "IMPLEMENTED", "CLOSED"];

export interface SuggestionRow {
  id: string;
  tenantId: string;
  guildId: string;
  authorDiscordUserId: string;
  content: string;
  status: SuggestionStatus;
  discordMessageId: string | null;
  reviewerUserId: string | null;
  createdAt: string;
  updatedAt: string;
}
/** Deterministic up/down tally over recorded votes (real DB values). */
export interface SuggestionTally {
  up: number;
  down: number;
  score: number;
}
export interface SuggestionWithTally {
  suggestion: SuggestionRow;
  tally: SuggestionTally;
}

// ── Polls ─────────────────────────────────────────────────────────────────────
export type PollStatus = "OPEN" | "CLOSED";
export interface PollOption {
  id: string;
  label: string;
}
export interface PollRow {
  id: string;
  tenantId: string;
  guildId: string;
  question: string;
  options: PollOption[];
  allowMultiple: boolean;
  status: PollStatus;
  discordMessageId: string | null;
  closesAt: string | null;
  createdByUserId: string | null;
  createdAt: string;
}
/** Deterministic per-option tally over recorded votes (real DB values). */
export interface PollTally {
  counts: Record<string, number>;
  totalVoters: number;
}
export interface PollWithTally {
  poll: PollRow;
  tally: PollTally;
}

// ── Scheduled content: announcements / reminders / events ─────────────────────
export interface AnnouncementRow {
  id: string;
  tenantId: string;
  guildId: string;
  announcementKey: string;
  channelKey: string;
  content: Record<string, unknown>;
  schedule: EngagementSchedule;
  enabled: boolean;
  nextFireAt: string | null;
  lastFiredAt: string | null;
  configVersion: number | null;
  createdAt: string;
  updatedAt: string;
}
export interface ReminderRow {
  id: string;
  tenantId: string;
  guildId: string;
  channelKey: string | null;
  discordUserId: string | null;
  content: string;
  schedule: EngagementSchedule;
  enabled: boolean;
  nextFireAt: string | null;
  lastFiredAt: string | null;
  createdByUserId: string | null;
  createdAt: string;
}
export type EventStatus = "SCHEDULED" | "CANCELLED" | "COMPLETED";
export type RsvpResponse = "going" | "interested" | "declined";
export interface EventRow {
  id: string;
  tenantId: string;
  guildId: string;
  title: string;
  description: string | null;
  startsAt: string;
  endsAt: string | null;
  announceChannelKey: string | null;
  discordMessageId: string | null;
  status: EventStatus;
  createdByUserId: string | null;
  createdAt: string;
}
export interface RsvpRow {
  id: string;
  eventId: string;
  discordUserId: string;
  response: RsvpResponse;
  createdAt: string;
}

// ── Statistics (read-only; every value is a real DB count/aggregate) ──────────
export interface ServerStatSnapshotView {
  id: string;
  memberCount: number | null;
  onlineCount: number | null;
  metrics: Record<string, unknown> | null;
  createdAt: string;
}
export interface EngagementStatistics {
  tickets: { open: number; byStatus: Record<string, number> };
  suggestions: { total: number; byStatus: Record<string, number> };
  polls: { byStatus: Record<string, number>; recent: { id: string; question: string; status: string; tally: PollTally }[] };
  recentSnapshots: ServerStatSnapshotView[];
}

/**
 * Wrap engagement modules into a minimal blueprint content so the server's
 * blueprint validator can resolve their role/channel/category references. Mirrors
 * the server's own `wrapEngagementInBlueprint`: referenced keys are declared so
 * they resolve, and the strict schema still rejects malformed module config.
 */
export function buildEngagementContent(modules: EngagementModules): unknown {
  const roleKeys = new Set<string>();
  const channelKeys = new Set<string>();
  const categoryKeys = new Set<string>();
  const t = modules.tickets;
  if (t) {
    if (t.panelChannelKey) channelKeys.add(t.panelChannelKey);
    if (t.ticketCategoryKey) categoryKeys.add(t.ticketCategoryKey);
    for (const k of t.supportRoleKeys) roleKeys.add(k);
  }
  if (modules.suggestions?.channelKey) channelKeys.add(modules.suggestions.channelKey);
  for (const k of modules.suggestions?.reviewRoleKeys ?? []) roleKeys.add(k);
  if (modules.polls?.channelKey) channelKeys.add(modules.polls.channelKey);
  for (const item of modules.announcements?.items ?? []) if (item.channelKey) channelKeys.add(item.channelKey);
  if (modules.events?.announceChannelKey) channelKeys.add(modules.events.announceChannelKey);
  if (modules.statistics?.memberCountChannelKey) channelKeys.add(modules.statistics.memberCountChannelKey);
  return {
    schemaVersion: "1",
    metadata: { name: "engagement-module", templateType: "custom" },
    roles: [...roleKeys].map((resourceKey, i) => ({ resourceKey, name: resourceKey.slice(0, 90) || `r${i}`, desiredPosition: i + 1 })),
    categories: [...categoryKeys].map((resourceKey, i) => ({ resourceKey, name: resourceKey.slice(0, 90) || `cat${i}`, desiredPosition: i })),
    channels: [...channelKeys].map((resourceKey, i) => ({ resourceKey, name: resourceKey.slice(0, 90) || `c${i}`, type: "text", desiredPosition: i })),
    modules,
  };
}

// All engagement routes are nested under a tenant + ODIN guild id. The client
// never posts a Discord token, and ticket opening / message posting are runtime /
// locked-boundary actions — this surface deliberately exposes no "post now" or
// "open ticket for user" call that would act live.
const engagement = {
  path: (tenantId: string, guildId: string) => `/api/v1/tenants/${tenantId}/guilds/${guildId}/engagement`,

  // ── Config (versioned; drafts + validation never touch Discord) ─────────────
  getConfig: (tenantId: string, guildId: string) =>
    request<EngagementConfigResponse>(`${engagement.path(tenantId, guildId)}/config`),
  getVersion: (tenantId: string, guildId: string, versionId: string) =>
    request<EngagementVersionView>(`${engagement.path(tenantId, guildId)}/config/versions/${versionId}`),
  createDraftVersion: (tenantId: string, guildId: string, content: unknown) =>
    request<EngagementVersionView>(`${engagement.path(tenantId, guildId)}/config/versions`, {
      method: "POST",
      body: JSON.stringify({ content }),
    }),
  validateVersion: (tenantId: string, guildId: string, versionId: string) =>
    request<EngagementValidationOutcome>(`${engagement.path(tenantId, guildId)}/config/versions/${versionId}/validate`, {
      method: "POST",
    }),
  /**
   * Link a validated draft to a blueprint version + Phase 4 deployment and mark it
   * active. This does NOT deploy or approve anything itself — the deployment id it
   * links must already have travelled the Phase 4 dry-run → approval → queue path.
   */
  activateVersion: (
    tenantId: string,
    guildId: string,
    versionId: string,
    input: { blueprintVersionId?: string | null; deploymentId?: string | null } = {},
  ) =>
    request<EngagementActivateResult>(`${engagement.path(tenantId, guildId)}/config/versions/${versionId}/activate`, {
      method: "POST",
      body: JSON.stringify({ blueprintVersionId: input.blueprintVersionId ?? null, deploymentId: input.deploymentId ?? null }),
    }),

  // ── Tickets (list / detail + staff lifecycle; opening is a runtime action) ──
  listTickets: (tenantId: string, guildId: string, filter: { status?: TicketStatus; requesterDiscordUserId?: string; limit?: number } = {}) => {
    const q = new URLSearchParams();
    if (filter.status) q.set("status", filter.status);
    if (filter.requesterDiscordUserId) q.set("requesterDiscordUserId", filter.requesterDiscordUserId);
    if (filter.limit) q.set("limit", String(filter.limit));
    const qs = q.toString();
    return request<{ tickets: TicketRow[] }>(`${engagement.path(tenantId, guildId)}/tickets${qs ? `?${qs}` : ""}`);
  },
  getTicket: (tenantId: string, guildId: string, ticketId: string) =>
    request<{ ticket: TicketRow }>(`${engagement.path(tenantId, guildId)}/tickets/${ticketId}`),
  claimTicket: (tenantId: string, guildId: string, ticketId: string) =>
    request<{ ticket: { id: string; status: TicketStatus } }>(`${engagement.path(tenantId, guildId)}/tickets/${ticketId}/claim`, { method: "POST" }),
  closeTicket: (tenantId: string, guildId: string, ticketId: string) =>
    request<{ ticket: { id: string; status: TicketStatus } }>(`${engagement.path(tenantId, guildId)}/tickets/${ticketId}/close`, { method: "POST" }),
  archiveTicket: (tenantId: string, guildId: string, ticketId: string) =>
    request<{ ticket: { id: string; status: TicketStatus } }>(`${engagement.path(tenantId, guildId)}/tickets/${ticketId}/archive`, { method: "POST" }),

  // ── Suggestions (list with live tallies / create / detail / close) ──────────
  listSuggestions: (tenantId: string, guildId: string, filter: { status?: SuggestionStatus; limit?: number } = {}) => {
    const q = new URLSearchParams();
    if (filter.status) q.set("status", filter.status);
    if (filter.limit) q.set("limit", String(filter.limit));
    const qs = q.toString();
    return request<{ suggestions: SuggestionWithTally[] }>(`${engagement.path(tenantId, guildId)}/suggestions${qs ? `?${qs}` : ""}`);
  },
  createSuggestion: (tenantId: string, guildId: string, content: string) =>
    request<{ suggestion: SuggestionRow }>(`${engagement.path(tenantId, guildId)}/suggestions`, {
      method: "POST",
      body: JSON.stringify({ content }),
    }),
  getSuggestion: (tenantId: string, guildId: string, suggestionId: string) =>
    request<SuggestionWithTally>(`${engagement.path(tenantId, guildId)}/suggestions/${suggestionId}`),
  closeSuggestion: (tenantId: string, guildId: string, suggestionId: string, status: SuggestionStatus) =>
    request<{ suggestion: SuggestionRow }>(`${engagement.path(tenantId, guildId)}/suggestions/${suggestionId}/close`, {
      method: "POST",
      body: JSON.stringify({ status }),
    }),

  // ── Polls (list with live tallies / create / detail / close) ────────────────
  listPolls: (tenantId: string, guildId: string, filter: { status?: PollStatus; limit?: number } = {}) => {
    const q = new URLSearchParams();
    if (filter.status) q.set("status", filter.status);
    if (filter.limit) q.set("limit", String(filter.limit));
    const qs = q.toString();
    return request<{ polls: PollWithTally[] }>(`${engagement.path(tenantId, guildId)}/polls${qs ? `?${qs}` : ""}`);
  },
  getPoll: (tenantId: string, guildId: string, pollId: string) =>
    request<PollWithTally>(`${engagement.path(tenantId, guildId)}/polls/${pollId}`),
  createPoll: (
    tenantId: string,
    guildId: string,
    input: { question: string; options: PollOption[]; allowMultiple?: boolean; closesAt?: string | null },
  ) =>
    request<{ poll: PollRow }>(`${engagement.path(tenantId, guildId)}/polls`, {
      method: "POST",
      body: JSON.stringify({
        question: input.question,
        options: input.options,
        allowMultiple: input.allowMultiple === true,
        closesAt: input.closesAt ?? null,
      }),
    }),
  closePoll: (tenantId: string, guildId: string, pollId: string) =>
    request<PollWithTally>(`${engagement.path(tenantId, guildId)}/polls/${pollId}/close`, { method: "POST" }),

  // ── Scheduled announcements (CRUD) ──────────────────────────────────────────
  listAnnouncements: (tenantId: string, guildId: string) =>
    request<{ announcements: AnnouncementRow[] }>(`${engagement.path(tenantId, guildId)}/announcements`),
  upsertAnnouncement: (
    tenantId: string,
    guildId: string,
    announcementKey: string,
    input: { channelKey: string; content: unknown; schedule: EngagementSchedule; enabled?: boolean },
  ) =>
    request<{ announcement: AnnouncementRow }>(`${engagement.path(tenantId, guildId)}/announcements/${announcementKey}`, {
      method: "PUT",
      body: JSON.stringify({ channelKey: input.channelKey, content: input.content, schedule: input.schedule, enabled: input.enabled !== false }),
    }),
  deleteAnnouncement: (tenantId: string, guildId: string, announcementKey: string) =>
    request<void>(`${engagement.path(tenantId, guildId)}/announcements/${announcementKey}`, { method: "DELETE" }),

  // ── Reminders (CRUD) ────────────────────────────────────────────────────────
  listReminders: (tenantId: string, guildId: string) =>
    request<{ reminders: ReminderRow[] }>(`${engagement.path(tenantId, guildId)}/reminders`),
  createReminder: (
    tenantId: string,
    guildId: string,
    input: { channelKey?: string | null; discordUserId?: string | null; content: string; schedule: EngagementSchedule; enabled?: boolean },
  ) =>
    request<{ reminder: ReminderRow }>(`${engagement.path(tenantId, guildId)}/reminders`, {
      method: "POST",
      body: JSON.stringify({
        channelKey: input.channelKey ?? null,
        discordUserId: input.discordUserId ?? null,
        content: input.content,
        schedule: input.schedule,
        enabled: input.enabled !== false,
      }),
    }),
  deleteReminder: (tenantId: string, guildId: string, reminderId: string) =>
    request<void>(`${engagement.path(tenantId, guildId)}/reminders/${reminderId}`, { method: "DELETE" }),

  // ── Community events (CRUD; RSVP is a member action via ingest) ─────────────
  listEvents: (tenantId: string, guildId: string) =>
    request<{ events: EventRow[] }>(`${engagement.path(tenantId, guildId)}/events`),
  createEvent: (
    tenantId: string,
    guildId: string,
    input: { title: string; description?: string | null; startsAt: string; endsAt?: string | null; announceChannelKey?: string | null },
  ) =>
    request<{ event: EventRow }>(`${engagement.path(tenantId, guildId)}/events`, {
      method: "POST",
      body: JSON.stringify({
        title: input.title,
        description: input.description ?? null,
        startsAt: input.startsAt,
        endsAt: input.endsAt ?? null,
        announceChannelKey: input.announceChannelKey ?? null,
      }),
    }),
  getEvent: (tenantId: string, guildId: string, eventId: string) =>
    request<{ event: EventRow; rsvps: RsvpRow[] }>(`${engagement.path(tenantId, guildId)}/events/${eventId}`),
  setEventStatus: (tenantId: string, guildId: string, eventId: string, status: "CANCELLED" | "COMPLETED") =>
    request<{ event: EventRow }>(`${engagement.path(tenantId, guildId)}/events/${eventId}/status`, {
      method: "POST",
      body: JSON.stringify({ status }),
    }),

  // ── Statistics (read-only; real DB counts) ──────────────────────────────────
  getStatistics: (tenantId: string, guildId: string) =>
    request<EngagementStatistics>(`${engagement.path(tenantId, guildId)}/statistics`),
};

// ── Phase 7 types (integrations, n8n, workflows, dead-letter, inbox) ──────────
// The control API is the ONLY place secrets live; every shape below is a SAFE
// projection. There is deliberately NO field anywhere here that could carry a
// signing secret, credential value or callback token — credentials surface as
// metadata only (kind / key version / present), and `test` is always the safe
// validate-and-preview action. Response shapes mirror routes-integration.ts
// exactly.

export const INTEGRATION_TYPE_NAMES = [
  "N8N",
  "RSS",
  "TWITCH",
  "YOUTUBE",
  "GAME_SERVER",
  "STEAM_WORKSHOP",
  "WEBHOOK_OUTBOUND",
  "EMAIL_NOTIFICATION",
  "BACKUP_MONITOR",
  "DEPLOYMENT_NOTIFICATION",
] as const;
export type IntegrationTypeName = (typeof INTEGRATION_TYPE_NAMES)[number];

/** Allowlisted workflow types (mirrors @odin/integrations WORKFLOW_TYPES). */
export const WORKFLOW_TYPE_NAMES = [
  "customer-onboarding-lifecycle",
  "operational-notifications",
  "rss-polling",
  "twitch-live-monitor",
  "youtube-live-monitor",
  "game-server-status",
  "workshop-update-monitor",
  "backup-verification",
  "deployment-notifications",
  "workflow-failure-handler",
] as const;
export type WorkflowTypeName = (typeof WORKFLOW_TYPE_NAMES)[number];

export type IntegrationStatus =
  | "DISCONNECTED"
  | "CONFIGURING"
  | "VALIDATING"
  | "HEALTHY"
  | "DEGRADED"
  | "FAILED"
  | "PAUSED"
  | "RATE_LIMITED"
  | "REVOKED";

/** The credential-free projection returned to the dashboard. Never a secret. */
export interface SafeIntegration {
  id: string;
  guildId: string | null;
  type: IntegrationTypeName;
  name: string;
  status: IntegrationStatus;
  requiresCredential: boolean;
  hasCredential: boolean;
  configuration: unknown;
  lastValidationAt: string | null;
  lastHealthyAt: string | null;
  lastErrorCode: string | null;
  safeErrorSummary: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface IntegrationConfigValidation {
  valid: boolean;
  errors: { path: string; message: string }[];
}

export interface SafeTestResult {
  ok: boolean;
  type: IntegrationTypeName;
  requiresCredential: boolean;
  validation: IntegrationConfigValidation;
  note: string;
}

/** Credential metadata — NO ciphertext, NO plaintext. Presence + version only. */
export interface CredentialMetadataView {
  kind: string;
  keyVersion: string;
  createdAtIso: string;
  version: number;
  present: boolean;
}

export interface HealthFindingView {
  severity: string;
  code: string;
  summary: string;
  resolved: boolean;
  detectedAt: string;
}
export interface BreakerView {
  scope: string;
  state: string;
  consecutiveFailures: number;
  openedAt: string | null;
}
export interface IntegrationHealth {
  status: string;
  findings: HealthFindingView[];
  breakers: BreakerView[];
}

export interface RssItemView {
  externalId: string;
  title: string;
  url: string | null;
  publishedAt: string | null;
  notified: boolean;
}
export interface StreamEventView {
  eventType: string;
  title: string | null;
  startedAt: string | null;
  notified: boolean;
}
export interface GameStatusView {
  online: boolean;
  playerCount: number | null;
  maxPlayers: number | null;
  map: string | null;
  confidence: string;
  ingestedAt: string;
}
export interface WorkshopUpdateView {
  itemId: string;
  modName: string;
  newVersion: string;
  changeClass: string;
  acknowledged: boolean;
  detectedAt: string;
}
export interface BackupStatusView {
  state: string;
  latestBackupAt: string | null;
  safeSummary: string | null;
  ingestedAt: string;
}

export interface WorkflowDefinitionView {
  id: string;
  workflowType: string;
  schemaVersion: string;
  state: string;
  enabled: boolean;
  n8nWorkflowId: string | null;
}
export interface WorkflowExecutionView {
  id: string;
  workflowType: string;
  status: string;
  attempts: number;
  sourceEventId: string | null;
  startedAt: string;
  completedAt: string | null;
  safeError: string | null;
}
export type FailureStateName = "RETRYABLE" | "RETRY_WAIT" | "DEAD_LETTER" | "MANUAL_REVIEW" | "RESOLVED" | "CANCELLED";
export interface WorkflowFailureView {
  id: string;
  workflowType: string;
  errorCode: string;
  safeSummary: string;
  state: FailureStateName;
  attempts: number;
  incidentGroup: string;
  replayEligible: boolean;
  unsafeCallback: boolean;
}

export type InboxItemType = "INFO" | "SUCCESS" | "WARNING" | "ERROR" | "ACTION_REQUIRED";
export interface InboxItemView {
  id: string;
  type: InboxItemType;
  source: string;
  title: string;
  summary: string;
  actionUrl: string | null;
  incidentGroup: string | null;
  read: boolean;
  archived: boolean;
  createdAt: string;
}
export interface IncidentGroupView {
  incidentGroup: string;
  count: number;
  latestAt: string;
  sample: InboxItemView;
}

// All integration routes are tenant-scoped. Nothing here ever transmits a secret
// back to the browser: the control API strips credential refs server-side.
const integrations = {
  base: (tenantId: string) => `/api/v1/tenants/${tenantId}/integrations`,
  workflowsBase: (tenantId: string) => `/api/v1/tenants/${tenantId}/workflows`,
  inboxBase: (tenantId: string) => `/api/v1/tenants/${tenantId}/inbox`,

  // ── Integration lifecycle ─────────────────────────────────────────────────
  list: (tenantId: string) => request<{ integrations: SafeIntegration[] }>(integrations.base(tenantId)),
  get: (tenantId: string, id: string) => request<{ integration: SafeIntegration }>(`${integrations.base(tenantId)}/${id}`),
  create: (tenantId: string, input: { type: IntegrationTypeName; name: string; guildId?: string | null }) =>
    request<{ integration: SafeIntegration }>(integrations.base(tenantId), { method: "POST", body: JSON.stringify(input) }),
  updateConfiguration: (tenantId: string, id: string, configuration: unknown) =>
    request<{ integration: SafeIntegration }>(`${integrations.base(tenantId)}/${id}/configuration`, {
      method: "PUT",
      body: JSON.stringify({ configuration }),
    }),
  // Record-only validation of the stored configuration — no live effect, no send.
  validate: (tenantId: string, id: string) =>
    request<{ integration: SafeIntegration; validation: IntegrationConfigValidation }>(`${integrations.base(tenantId)}/${id}/validate`, { method: "POST" }),
  // SAFE test — validate + preview only. Explicit action; never sends live.
  test: (tenantId: string, id: string) =>
    request<SafeTestResult>(`${integrations.base(tenantId)}/${id}/test`, { method: "POST" }),
  pause: (tenantId: string, id: string) =>
    request<{ integration: SafeIntegration }>(`${integrations.base(tenantId)}/${id}/pause`, { method: "POST" }),
  resume: (tenantId: string, id: string) =>
    request<{ integration: SafeIntegration }>(`${integrations.base(tenantId)}/${id}/resume`, { method: "POST" }),
  revoke: (tenantId: string, id: string) =>
    request<{ integration: SafeIntegration }>(`${integrations.base(tenantId)}/${id}/revoke`, { method: "POST" }),

  // ── Credentials (metadata only; a secret value is NEVER returned) ──────────
  listCredentials: (tenantId: string, id: string) =>
    request<{ credentials: CredentialMetadataView[] }>(`${integrations.base(tenantId)}/${id}/credentials`),
  // Store or rotate: the plaintext is POSTed once and never echoed back.
  storeCredential: (tenantId: string, id: string, kind: string, value: string) =>
    request<{ credential: CredentialMetadataView }>(`${integrations.base(tenantId)}/${id}/credentials`, {
      method: "PUT",
      body: JSON.stringify({ kind, value }),
    }),
  revokeCredential: (tenantId: string, id: string, kind: string) =>
    request<{ revoked: number }>(`${integrations.base(tenantId)}/${id}/credentials?kind=${encodeURIComponent(kind)}`, { method: "DELETE" }),

  // ── Health + provider status reads ────────────────────────────────────────
  getHealth: (tenantId: string, id: string) => request<IntegrationHealth>(`${integrations.base(tenantId)}/${id}/health`),
  getRss: (tenantId: string, id: string) => request<{ items: RssItemView[] }>(`${integrations.base(tenantId)}/${id}/rss`),
  getStream: (tenantId: string, id: string) => request<{ events: StreamEventView[] }>(`${integrations.base(tenantId)}/${id}/stream`),
  getGame: (tenantId: string, id: string) => request<{ status: GameStatusView | null }>(`${integrations.base(tenantId)}/${id}/game`),
  getWorkshop: (tenantId: string, id: string) => request<{ updates: WorkshopUpdateView[] }>(`${integrations.base(tenantId)}/${id}/workshop`),
  getBackup: (tenantId: string, id: string) => request<{ status: BackupStatusView | null }>(`${integrations.base(tenantId)}/${id}/backup`),

  // ── Workflows + execution history + dead-letter ───────────────────────────
  listWorkflows: (tenantId: string) => request<{ workflows: WorkflowDefinitionView[] }>(integrations.workflowsBase(tenantId)),
  registerWorkflow: (tenantId: string, workflowType: string, n8nWorkflowId?: string | null) =>
    request<{ id: string; workflowType: string; state: string }>(integrations.workflowsBase(tenantId), {
      method: "POST",
      body: JSON.stringify({ workflowType, n8nWorkflowId: n8nWorkflowId ?? null }),
    }),
  listExecutions: (tenantId: string, filter: { workflowType?: string; limit?: number; cursor?: string | null } = {}) => {
    const q = new URLSearchParams();
    if (filter.workflowType) q.set("workflowType", filter.workflowType);
    if (filter.limit) q.set("limit", String(filter.limit));
    if (filter.cursor) q.set("cursor", filter.cursor);
    const qs = q.toString();
    return request<{ items: WorkflowExecutionView[]; nextCursor: string | null }>(`${integrations.workflowsBase(tenantId)}/executions${qs ? `?${qs}` : ""}`);
  },
  getExecution: (tenantId: string, executionId: string) =>
    request<{ execution: WorkflowExecutionView }>(`${integrations.workflowsBase(tenantId)}/executions/${executionId}`),
  listDeadLetter: (tenantId: string, filter: { state?: FailureStateName; limit?: number } = {}) => {
    const q = new URLSearchParams();
    if (filter.state) q.set("state", filter.state);
    if (filter.limit) q.set("limit", String(filter.limit));
    const qs = q.toString();
    return request<{ failures: WorkflowFailureView[] }>(`${integrations.workflowsBase(tenantId)}/dead-letter${qs ? `?${qs}` : ""}`);
  },
  getFailure: (tenantId: string, failureId: string) =>
    request<{ failure: WorkflowFailureView }>(`${integrations.workflowsBase(tenantId)}/dead-letter/${failureId}`),
  // Safe, eligibility-gated replay — only ENQUEUES a job; an unsafe callback can never be replayed.
  retryFailure: (tenantId: string, failureId: string) =>
    request<{ requested: boolean; failureId: string; state: FailureStateName }>(`${integrations.workflowsBase(tenantId)}/dead-letter/${failureId}/retry`, { method: "POST" }),

  // ── Inbox ─────────────────────────────────────────────────────────────────
  listInbox: (tenantId: string, filter: { read?: boolean; limit?: number } = {}) => {
    const q = new URLSearchParams();
    if (filter.read !== undefined) q.set("read", String(filter.read));
    if (filter.limit) q.set("limit", String(filter.limit));
    const qs = q.toString();
    return request<{ items: InboxItemView[] }>(`${integrations.inboxBase(tenantId)}${qs ? `?${qs}` : ""}`);
  },
  listIncidents: (tenantId: string) => request<{ incidents: IncidentGroupView[] }>(`${integrations.inboxBase(tenantId)}/incidents`),
  markAllInboxRead: (tenantId: string) => request<{ updated: number }>(`${integrations.inboxBase(tenantId)}/read-all`, { method: "POST" }),
  markInboxRead: (tenantId: string, itemId: string) => request<{ item: InboxItemView }>(`${integrations.inboxBase(tenantId)}/${itemId}/read`, { method: "POST" }),
  archiveInbox: (tenantId: string, itemId: string) => request<{ item: InboxItemView }>(`${integrations.inboxBase(tenantId)}/${itemId}/archive`, { method: "POST" }),
};

// ── Endpoints ────────────────────────────────────────────────────────────────
// ── Phase 8 types (Cloudflare production: status, incidents, artifacts, turnstile)
// Every shape below is a SAFE, secret-free projection that mirrors the control-api's
// routes-cloudflare.ts. The public status + incident views carry NO tenant name,
// guild id, hostname, queue name, credential or stack trace. Artifact views expose
// metadata only — never the encryption key, and never a permanent public URL.

export type ComponentState = "operational" | "degraded" | "unknown";

export const INCIDENT_SEVERITIES = [
  "INFORMATION",
  "DEGRADED",
  "PARTIAL_OUTAGE",
  "MAJOR_OUTAGE",
  "MAINTENANCE",
] as const;
export type IncidentSeverity = (typeof INCIDENT_SEVERITIES)[number];

export const INCIDENT_COMPONENTS = [
  "dashboard",
  "control-api",
  "bot-runtime",
  "worker",
  "n8n",
  "object-storage",
  "database",
] as const;
export type IncidentComponent = (typeof INCIDENT_COMPONENTS)[number];

export const INCIDENT_STATES = ["OPEN", "MONITORING", "RESOLVED"] as const;
export type IncidentState = (typeof INCIDENT_STATES)[number];

/** Secret-free public incident projection (status page + dashboard banner). */
export interface PublicIncidentView {
  title: string;
  severity: IncidentSeverity;
  components: IncidentComponent[];
  state: IncidentState;
  startedAtIso: string;
  updatedAtIso: string;
}

export interface PublicPlatformComponent {
  name: string;
  state: ComponentState;
}

/** Shape of `GET /status` — public and secret-free. */
export interface PublicPlatformStatus {
  service: string;
  environment: string;
  version: string;
  liveDeploymentLocked: boolean;
  components: PublicPlatformComponent[];
  incidents: PublicIncidentView[];
}

/** Operator-only full incident view (`/api/v1/incidents`). */
export interface PlatformIncidentView {
  id: string;
  title: string;
  description: string;
  severity: IncidentSeverity;
  components: IncidentComponent[];
  state: IncidentState;
  publicVisible: boolean;
  startedAtIso: string;
  updatedAtIso: string;
  resolvedAtIso: string | null;
}

// ── Artifacts (metadata only — never the encryption key, never a durable URL) ──
export const ARTIFACT_RESOURCE_TYPES = [
  "guild-snapshots",
  "dry-run-exports",
  "release-reports",
  "rollback-reports",
  "audit-exports",
  "tenant-exports",
  "integration-exports",
  "backup-manifests",
  "customer-assets",
  "documents",
] as const;
export type ArtifactResourceType = (typeof ARTIFACT_RESOURCE_TYPES)[number];

export const RETENTION_CLASSES = ["operational", "release-linked", "long-term", "temporary", "audit"] as const;
export type RetentionClass = (typeof RETENTION_CLASSES)[number];

export interface ArtifactMetadataView {
  id: string;
  tenantId: string;
  guildId: string | null;
  resourceType: ArtifactResourceType;
  storageProvider: string;
  /** Internal object key — NOT a public URL; never rendered as a link. */
  objectKey: string;
  size: number;
  mimeType: string;
  sha256: string;
  encrypted: boolean;
  encryptionKeyVersion: string | null;
  immutable: boolean;
  retentionClass: RetentionClass;
  legalHold: boolean;
  sourceResource: string | null;
  createdByUserId: string | null;
  createdAtIso: string;
  expiresAtIso: string | null;
  deletedAtIso: string | null;
  lastIntegrityAtIso: string | null;
}

export interface SignedUrlView {
  url: string;
  method: "PUT" | "GET";
  expiresAtEpochSec: number;
  key: string;
}
export interface RequestUploadResult {
  sessionId: string;
  upload: SignedUrlView;
  maxBytes: number;
  expiresAtIso: string;
}
export interface RequestUploadInput {
  resourceType: ArtifactResourceType;
  filename: string;
  mimeType: string;
  sizeBytes?: number;
}
/** Short-lived download directive — a signed URL OR a server-mediated stream. */
export type DownloadDirectiveView =
  | { mode: "signed_url"; url: string; method: "GET"; expiresAtEpochSec: number; filename: string }
  | { mode: "stream"; filename: string; streamPath: string };

export interface TurnstileVerifyResult {
  ok: boolean;
  verified: boolean;
}

const A = (tenantId: string): string => `/api/v1/tenants/${tenantId}/artifacts`;

const artifacts = {
  list: (tenantId: string, resourceType?: ArtifactResourceType) =>
    request<{ artifacts: ArtifactMetadataView[] }>(
      `${A(tenantId)}${resourceType ? `?resourceType=${encodeURIComponent(resourceType)}` : ""}`,
    ),
  get: (tenantId: string, artifactId: string) =>
    request<{ artifact: ArtifactMetadataView }>(`${A(tenantId)}/${artifactId}`),
  requestUpload: (tenantId: string, input: RequestUploadInput) =>
    request<RequestUploadResult>(`${A(tenantId)}/request-upload`, { method: "POST", body: JSON.stringify(input) }),
  finalizeUpload: (tenantId: string, input: { sessionId: string; immutable?: boolean }) =>
    request<{ artifact: ArtifactMetadataView }>(`${A(tenantId)}/finalize-upload`, {
      method: "POST",
      body: JSON.stringify(input),
    }),
  requestDownload: (tenantId: string, artifactId: string, input: { downloadFilename?: string } = {}) =>
    request<DownloadDirectiveView>(`${A(tenantId)}/${artifactId}/request-download`, {
      method: "POST",
      body: JSON.stringify(input),
    }),
  remove: (tenantId: string, artifactId: string) =>
    request<{ deleted: boolean } | Record<string, unknown>>(`${A(tenantId)}/${artifactId}`, { method: "DELETE" }),
  setLegalHold: (tenantId: string, artifactId: string, hold: boolean) =>
    request<{ artifact: ArtifactMetadataView }>(`${A(tenantId)}/${artifactId}/legal-hold`, {
      method: "POST",
      body: JSON.stringify({ hold }),
    }),
  /** Absolute URL for a server-streamed download (used with credentials). */
  streamUrl: (streamPath: string): string => `${API_BASE}${streamPath}`,
};

const incidents = {
  list: () => request<{ incidents: PlatformIncidentView[] }>("/api/v1/incidents"),
  get: (incidentId: string) => request<{ incident: PlatformIncidentView }>(`/api/v1/incidents/${incidentId}`),
  create: (input: {
    title: string;
    description?: string;
    severity: IncidentSeverity;
    components: IncidentComponent[];
    publicVisible?: boolean;
    initialMessage?: string;
  }) => request<{ incident: PlatformIncidentView }>("/api/v1/incidents", { method: "POST", body: JSON.stringify(input) }),
  update: (
    incidentId: string,
    patch: {
      state?: IncidentState;
      publicVisible?: boolean;
      message?: string;
      severity?: IncidentSeverity;
      components?: IncidentComponent[];
    },
  ) => request<{ incident: PlatformIncidentView }>(`/api/v1/incidents/${incidentId}`, { method: "PATCH", body: JSON.stringify(patch) }),
};

const turnstile = {
  /** Submit a Turnstile token to the additive verify endpoint. Never echoes the token. */
  verify: (token: string) =>
    request<TurnstileVerifyResult>("/api/v1/turnstile/verify", {
      method: "POST",
      body: JSON.stringify({ turnstileToken: token }),
    }),
};

// ── Linked accounts (canonical ODIN account, Part 1.3) ──────────────────────
/**
 * One sign-in method on the account. This mirrors the server's SAFE projection:
 * it deliberately carries no provider subject (the Discord/Google user id), so a
 * linked account can never be identified — let alone impersonated — from
 * anything the browser holds.
 */
export type IdentityProviderName = "discord" | "google" | "email";

export interface IdentitySummary {
  id: string;
  provider: IdentityProviderName;
  providerEmail: string | null;
  linkedAt: string;
  lastUsedAt: string | null;
  /** True when this identity alone could get the customer back in. */
  canRecover: boolean;
  /** False when removing it would leave no usable sign-in method. */
  canUnlink: boolean;
}

const identities = {
  list: () => request<{ identities: IdentitySummary[] }>("/api/v1/me/identities"),
  /**
   * Full-page URL that starts a link. It must be NAVIGATED to (window.location or
   * a plain link), not fetched: the provider answers with a redirect the browser
   * has to follow, and the session cookie has to ride along so the server can
   * bind the link to the signed-in account. The account is taken from that
   * cookie on the server — never from anything passed here.
   */
  startUrl: (provider: "discord" | "google", redirect = "/app/settings/linked-accounts"): string =>
    `${API_BASE}/api/v1/me/identities/${provider}/start?redirect=${encodeURIComponent(redirect)}`,
  unlink: (identityId: string) =>
    request<{ status: string; identities: IdentitySummary[] }>(`/api/v1/me/identities/${identityId}`, {
      method: "DELETE",
    }),
};

// ── Delete account (right to erasure) ───────────────────────────────────────
// These mirror the server's projections in
// apps/control-api/src/auth/account-deletion-service.ts exactly. The browser
// never decides what deletion costs, never decides whether it is allowed, and
// never decides that the typed confirmation matched — all three are the
// server's answers, shown here so the customer reads the same facts the server
// will act on.

export interface AccountDeletionGuildView {
  discordGuildId: string;
  name: string;
}

/** One workspace, and what deleting the account would do to it. */
export interface AccountWorkspaceView {
  tenantId: string;
  name: string;
  role: string;
  memberCount: number;
  ownerCount: number;
  guilds: AccountDeletionGuildView[];
  subscription: { planCode: string; status: string } | null;
  inFlightDeployments: Array<{ id: string; status: string }>;
}

export interface AccountDeletionBlockerView {
  code:
    | "active_subscription"
    | "deployment_in_flight"
    | "sole_owner_with_members"
    | "refund_in_progress"
    | "operator_account"
    | "account_restricted"
    | string;
  message: string;
  resolution: string;
}

export interface AccountDeletionPreviewView {
  userId: string;
  /** What the customer must type back — their own email, or username if ODIN holds no address. */
  confirmationPhrase: string;
  workspacesToDelete: AccountWorkspaceView[];
  workspacesToLeave: AccountWorkspaceView[];
  identities: Array<{ id: string; provider: IdentityProviderName; providerEmail: string | null }>;
  consequences: string[];
  preserved: string[];
  blockers: AccountDeletionBlockerView[];
  canDelete: boolean;
  /** True when the customer has to sign in again before the delete will be accepted. */
  requiresRecentSignIn: boolean;
}

export interface AccountDeletionReceiptView {
  tenantsDeleted: string[];
  membershipsRemoved: number;
  identitiesRemoved: number;
  sessionsRevoked: number;
  discordAuthorizationsRevoked: number;
  purged: Record<string, number>;
}

export interface AccountDeletionResultView {
  status: string;
  receipt: AccountDeletionReceiptView;
}

const account = {
  /**
   * Read-only, and safe to call the moment the panel opens. Seeing what erasure
   * would cost must never itself cost anything.
   */
  deletePreview: () => request<AccountDeletionPreviewView>("/api/v1/me/delete-preview"),
  /**
   * Irreversible. `confirmPhrase` is the email address typed back by the
   * customer; the server compares it and rejects anything else, so the browser
   * never concludes on its own that a deletion was confirmed. A session older
   * than fifteen minutes comes back as 403 `recent_auth_required`.
   */
  delete: (confirmPhrase: string) =>
    request<AccountDeletionResultView>("/api/v1/me", {
      method: "DELETE",
      body: JSON.stringify({ confirmPhrase }),
    }),
};

// ── Phase 9: billing, subscriptions & entitlements ──────────────────────────
// These view types mirror the SERVER's public/safe projections exactly (see
// apps/control-api/src/billing/*-service.ts). None of them carry a provider price
// id, customer id, card/bank data, secret, or raw entitlement flag — the server
// strips those before they reach the browser. A redirect to Stripe always uses the
// hosted URL the API returns; no publishable/secret key is ever needed here.

export type BillingPeriod = "monthly" | "yearly";
export type SubscriptionState =
  | "NONE" | "FREE" | "INCOMPLETE" | "INCOMPLETE_EXPIRED" | "TRIALING" | "ACTIVE"
  | "PAST_DUE" | "UNPAID" | "PAUSED" | "CANCELLING" | "CANCELLED" | "GRACE_PERIOD"
  | "MANUAL_ACTIVE" | "MANUAL_EXPIRED";

export interface PublicPlanPrice {
  period: BillingPeriod;
  amountMinor: number;
  currency: string;
  formatted: string;
}
export interface PublicPlanView {
  planCode: string;
  displayName: string;
  description: string;
  supportLevel: string;
  billingAvailable: boolean;
  prices: PublicPlanPrice[];
  capabilities: string[];
  primaryLimits: { label: string; value: number }[];
  trialAvailable: boolean;
  cancellationSummary: string;
  taxNote: string;
}
export interface PublicPricing {
  catalogueVersion: string;
  plans: PublicPlanView[];
  taxNote: string;
  currencyNote: string;
}

/** A change the customer has already asked for that has not taken effect yet. */
export interface ScheduledChangeView {
  kind: "cancellation";
  effectiveAtIso: string | null;
  resultingPlanCode: string;
  reversible: boolean;
  description: string;
}

export interface SafeSubscriptionView {
  planCode: string;
  status: SubscriptionState;
  billingPeriod: string | null;
  currentPeriodEndIso: string | null;
  trialEndIso: string | null;
  cancelAtPeriodEnd: boolean;
  cancellationRequested: boolean;
  inGrace: boolean;
  graceEndsIso: string | null;
  isFreeFallback: boolean;
  /** VAT-inclusive display price, or null when it cannot be quoted accurately. */
  priceDisplay: string | null;
  priceCadenceLabel: string | null;
  priceInclVatMinor: number | null;
  currency: string | null;
  vatInclusive: boolean;
  vatRatePct: number;
  vatNote: string;
  paidThroughIso: string | null;
  nextBillingIso: string | null;
  renews: boolean;
  /**
   * Presence only. ODIN never receives, stores or displays card details — no brand,
   * last four digits or expiry ever crosses this boundary.
   */
  paymentMethodOnFile: boolean;
  paymentMethodNote: string;
  scheduledChange: ScheduledChangeView | null;
  canCancel: boolean;
  canResumeCancellation: boolean;
}

export interface CancellationReview {
  currentPlan: string;
  status: SubscriptionState;
  billingInterval: string | null;
  priceDisplay: string | null;
  vatNote: string;
  paidThroughIso: string | null;
  effectiveAtIso: string | null;
  downgradesTo: string;
  renewalStops: boolean;
  accessRemainsUntilIso: string | null;
  effects: string[];
  dataRetention: string[];
  reactivation: string;
  canCancel: boolean;
  alreadyScheduled: boolean;
  note: string;
}

/** Result of scheduling — or calling off — a period-end cancellation. */
export interface CancellationResult {
  scheduled: boolean;
  alreadyInThatState: boolean;
  usePortal: boolean;
  planCode: string;
  status: string;
  effectiveAtIso: string | null;
  accessRemainsUntilIso: string | null;
  reversible: boolean;
  message: string;
}

export type ImpactKind = "no-impact" | "over-limit" | "read-only" | "paused" | "manual-choice-required";
export interface EntitlementImpact {
  key: string;
  currentValue: number | boolean;
  targetValue: number | boolean;
  currentUsage: number | null;
  kind: ImpactKind;
  explanation: string;
}
/** Which way along the plan ladder a change goes. Computed server-side on the
 * canonical ladder — the dashboard must never infer it from the order of the
 * public pricing list. An unrecognised plan is reported as "lateral" rather than
 * guessed at, so nobody is told they are downgrading because of a lookup miss. */
export type PlanChangeDirection = "upgrade" | "downgrade" | "lateral";
export interface DowngradeAssessment {
  fromPlan: string;
  toPlan: string;
  direction: PlanChangeDirection;
  impacts: EntitlementImpact[];
  requiresManualChoice: boolean;
  effectiveNote: string;
}

export type UsageFreshness = "OPEN" | "ESTIMATED" | "FINALIZING" | "FINAL" | "CORRECTED";
export type QuotaState = "ok" | "warning" | "limit-reached";
export type OverageBehavior = "deny" | "queue" | "allow-overage" | "require-upgrade" | "admin-override";
export interface QuotaResult {
  metric: string;
  allowance: number | null;
  consumed: number;
  remaining: number | null;
  warnThreshold: number;
  resetEpochSec: number | null;
  overageBehavior: OverageBehavior;
  state: QuotaState;
  freshness: UsageFreshness;
}
export interface UsageSummaryItem {
  metric: string;
  consumed: number;
  allowance: number | null;
  quota: QuotaResult;
}
export interface UsageSummary {
  periodKey: string;
  items: UsageSummaryItem[];
}

export interface InvoiceMoney {
  minor: number;
  formatted: string;
}
export interface SafeInvoiceView {
  id: string;
  number: string | null;
  status: string;
  currency: string;
  totals: {
    subtotal: InvoiceMoney;
    tax: InvoiceMoney;
    total: InvoiceMoney;
    amountPaid: InvoiceMoney;
    amountDue: InvoiceMoney;
  };
  periodStartIso: string | null;
  periodEndIso: string | null;
  paidAtIso: string | null;
  createdAtIso: string;
}

export type EnforcementState =
  | "ALLOWED" | "ALLOWED_WITH_WARNING" | "LIMIT_REACHED" | "GRACE_PERIOD"
  | "READ_ONLY" | "PAUSED" | "DENIED" | "SAFETY_OVERRIDE";
export type EntitlementSource =
  | "safety-override" | "manual-override" | "subscription" | "grace" | "free-default";
export interface EvaluatedEntitlement {
  key: string;
  value: number | boolean | string;
  source: EntitlementSource;
  limit: number | null;
  usage: number | null;
  remaining: number | null;
  enforcement: EnforcementState;
  explanation: string;
}
export interface EntitlementSnapshotView {
  tenantId: string;
  planCode: string;
  catalogueVersion: string;
  subscriptionState: SubscriptionState;
  entitlements: EvaluatedEntitlement[];
  evaluatedAtEpochSec: number;
  hash: string;
}

export interface CheckoutResultView {
  checkoutUrl: string;
  expiresAtEpochSec: number;
}
export interface PortalResultView {
  portalUrl: string;
  expiresAtEpochSec: number;
}

export interface ManualAgreementView {
  id: string;
  planCode: string;
  status: string;
  startAtIso: string;
  expiryAtIso: string;
  billingReference: string | null;
  invoiceReference: string | null;
  reason: string;
}
export interface AdjustmentView {
  id: string;
  type: string;
  state: string;
  reason: string;
  providerInvoiceId: string | null;
  createdAtIso: string;
  updatedAtIso: string;
}

/**
 * Refunds (Part 8).
 *
 * Note what is NOT in these types: no payment id, no card, no bank detail, no
 * payout destination. There is nowhere in this client to type where money should
 * go, because there is nowhere in the API to send it — a refund can only return
 * along the path the payment arrived on. `providerRefundId` is optional because
 * the server omits it entirely for customers.
 */
export type RefundRequestState =
  | "SUBMITTED" | "UNDER_REVIEW" | "INFORMATION_REQUESTED" | "APPROVED" | "PARTIALLY_APPROVED"
  | "DENIED" | "PROCESSING" | "COMPLETED" | "FAILED" | "CANCELLED";

export interface RefundMoney {
  minor: number;
  formatted: string;
}

export interface RefundPreviewView {
  invoiceId: string;
  currency: string;
  amountPaid: RefundMoney;
  alreadyRefunded: RefundMoney;
  maxRefundable: RefundMoney;
  outcome: string;
  reasons: string[];
  canRequestManualReview: boolean;
  withdrawalDaysRemaining: number | null;
  /** False when ODIN would have to arrange this by hand. Shown before submitting. */
  canBeSentAutomatically: boolean;
  consequences: string[];
  existingOpenRequestId: string | null;
  reasonOptions: { code: string; label: string }[];
}

export interface RefundView {
  id: string;
  state: RefundRequestState;
  stateLabel: string;
  reason: string;
  reasonLabel: string;
  explanation: string;
  currency: string;
  amountPaid: RefundMoney;
  requested: RefundMoney;
  approved: RefundMoney | null;
  refunded: RefundMoney;
  decisionNote: string | null;
  failureReason: string | null;
  canCancel: boolean;
  terminal: boolean;
  createdAtIso: string;
  updatedAtIso: string;
  decidedAtIso: string | null;
  completedAtIso: string | null;
  providerRefundId?: string | null;
}

/**
 * Result of a provider re-read. Safe by construction: a plan code, a subscription
 * state, advisory alert codes and a machine-readable reason. It carries no provider
 * object, price id, customer id, invoice amount or card detail.
 */
export interface SubscriptionSyncView {
  synced: boolean;
  reason: string | null;
  planCode: string | null;
  status: string | null;
  alerts: string[];
}

const billingBase = (tenantId: string): string => `/api/v1/tenants/${tenantId}/billing`;

const billing = {
  /** PUBLIC, secret-free pricing projection (no provider price ids, no raw flags). */
  plans: () => request<PublicPricing>("/api/v1/billing/plans"),
  subscription: (tenantId: string) =>
    request<{ subscription: SafeSubscriptionView }>(`${billingBase(tenantId)}/subscription`),
  entitlements: (tenantId: string) =>
    request<{ entitlements: EntitlementSnapshotView }>(`${billingBase(tenantId)}/entitlements`),
  usage: (tenantId: string) => request<UsageSummary>(`${billingBase(tenantId)}/usage`),
  usageMetric: (tenantId: string, metric: string) =>
    request<{ usage: UsageSummaryItem }>(`${billingBase(tenantId)}/usage/${encodeURIComponent(metric)}`),
  invoices: (tenantId: string) =>
    request<{ invoices: SafeInvoiceView[] }>(`${billingBase(tenantId)}/invoices`),
  invoice: (tenantId: string, invoiceId: string) =>
    request<{ invoice: SafeInvoiceView }>(`${billingBase(tenantId)}/invoices/${invoiceId}`),
  /** Private, no-store hosted invoice URL — requested only on an explicit click. */
  invoiceUrl: (tenantId: string, invoiceId: string) =>
    request<{ hostedInvoiceUrl: string | null }>(`${billingBase(tenantId)}/invoices/${invoiceId}/url`),
  planChangeReview: (tenantId: string, target: string) =>
    request<{ assessment: DowngradeAssessment }>(
      `${billingBase(tenantId)}/plan-change-review?target=${encodeURIComponent(target)}`,
    ),
  cancellationReview: (tenantId: string) =>
    request<{ review: CancellationReview }>(`${billingBase(tenantId)}/cancellation-review`),
  /** Start hosted checkout. The provider PRICE is resolved server-side; we send only
   * the plan + period and receive a hosted URL to redirect to. */
  startCheckout: (tenantId: string, input: { planCode: string; period: string }) =>
    request<CheckoutResultView>(`${billingBase(tenantId)}/checkout-sessions`, {
      method: "POST",
      body: JSON.stringify(input),
    }),
  /** Start a one-time hosted checkout for a BALDER usage pack (paid plans only). The
   * pack price is resolved server-side; we send only the pack code and get a hosted URL. */
  startPackCheckout: (tenantId: string, input: { packCode: string }) =>
    request<CheckoutResultView>(`${billingBase(tenantId)}/pack-checkout-sessions`, {
      method: "POST",
      body: JSON.stringify(input),
    }),
  /** Open the hosted Customer Portal (server resolves the customer; we get a URL). */
  openPortal: (tenantId: string) =>
    request<PortalResultView>(`${billingBase(tenantId)}/portal-sessions`, { method: "POST" }),
  /**
   * What a refund on this payment would actually mean — fetched BEFORE the customer
   * commits to anything, so the consequences and the real refundable amount are on
   * screen while they are still deciding.
   */
  refundPreview: (tenantId: string, invoiceId: string) =>
    request<{ preview: RefundPreviewView }>(
      `${billingBase(tenantId)}/invoices/${encodeURIComponent(invoiceId)}/refund-preview`,
    ),
  refunds: (tenantId: string) => request<{ refunds: RefundView[] }>(`${billingBase(tenantId)}/refunds`),
  refund: (tenantId: string, refundId: string) =>
    request<{ refund: RefundView }>(`${billingBase(tenantId)}/refunds/${encodeURIComponent(refundId)}`),
  /**
   * Ask for money back. This creates a REQUEST — no money moves, and there is
   * deliberately no client call anywhere in this file that executes a refund, because
   * executing one is a platform-operator action the customer's session cannot perform.
   * `requestedAmountMinor` is a request, not an instruction: the server clamps it to
   * what is actually refundable.
   */
  requestRefund: (
    tenantId: string,
    input: { invoiceId: string; reason: string; explanation: string; requestedAmountMinor?: number | null },
  ) =>
    request<{ refund: RefundView }>(`${billingBase(tenantId)}/refunds`, {
      method: "POST",
      body: JSON.stringify(input),
    }),
  /** Withdraw your own request. One click, no retention gauntlet (Part 8.5). */
  withdrawRefund: (tenantId: string, refundId: string) =>
    request<{ refund: RefundView }>(
      `${billingBase(tenantId)}/refunds/${encodeURIComponent(refundId)}/withdrawal`,
      { method: "POST" },
    ),
  adjustments: (tenantId: string) =>
    request<{ adjustments: AdjustmentView[] }>(`${billingBase(tenantId)}/adjustments`),
  requestAdjustment: (tenantId: string, input: { type: string; reason: string; providerInvoiceId?: string }) =>
    request<{ adjustment: AdjustmentView }>(`${billingBase(tenantId)}/adjustments`, {
      method: "POST",
      body: JSON.stringify(input),
    }),
  manual: (tenantId: string) =>
    request<{ agreements: ManualAgreementView[] }>(`${billingBase(tenantId)}/manual`),
  /**
   * Re-read this workspace's subscription from the payment provider and apply the
   * result. Used when a provider notification was delayed or missed, so a paid plan
   * activates without anyone being onboarded by hand.
   *
   * READ-ONLY at the provider: it never charges, changes, cancels or refunds
   * anything. The customer is resolved SERVER-side from this workspace's own billing
   * record — the browser sends no customer, subscription, price or plan.
   */
  sync: (tenantId: string) => request<SubscriptionSyncView>(`${billingBase(tenantId)}/sync`, { method: "POST" }),
  /**
   * Schedule the plan to stop renewing at the END of the period already paid for.
   * Never immediate, never a refund, never a new charge — and reversible with
   * `resumePlan` right up until it takes effect. Requires explicit confirmation.
   */
  cancelPlan: (tenantId: string) =>
    request<CancellationResult>(`${billingBase(tenantId)}/cancellation`, {
      method: "POST",
      body: JSON.stringify({ confirm: true }),
    }),
  /** Call off a scheduled cancellation. Restores the normal renewal; charges nothing. */
  resumePlan: (tenantId: string) =>
    request<CancellationResult>(`${billingBase(tenantId)}/cancellation`, { method: "DELETE" }),
};

// ── Phase 10B types (ODIN Forge QA, launch readiness, safe observability) ─────
// SAFETY: this whole surface is PLATFORM-admin, read-only, and secret-free by
// construction on the server. A Forge run is mock + synthetic + locks-engaged and
// is NEVER a launch authorization; launch sign-off is human-only and lives outside
// the code. The observability view is coarse and NON-identifying — it never carries
// a tenant name/id, secret, payload, or internal host. This client exposes NO
// "launch", "approve", or "start run" call. Dates cross the wire as ISO strings.

export type ForgeRunState = "QUEUED" | "RUNNING" | "PASSED" | "FAILED" | "ERRORED" | "CANCELLED";
export type ForgeFindingSeverity = "critical" | "high" | "medium" | "low" | "info";
/** AI-critic findings are ADVISORY ONLY and must be human-confirmed; they never fail a run. */
export type ForgeFindingSource = "deterministic" | "ai-critic";

export interface ForgeRunRecord {
  id: string;
  environment: string;
  state: ForgeRunState;
  scenariosRun: number;
  scenariosPassed: number;
  scenariosFailed: number;
  invariantChecks: number;
  invariantFailures: number;
  summaryHash: string | null;
  triggeredByUserId: string | null;
  startedAtIso: string;
  completedAtIso: string | null;
}
export interface ForgeFindingRecord {
  id: string;
  runId: string;
  severity: ForgeFindingSeverity;
  category: string;
  title: string;
  detail: string;
  source: ForgeFindingSource;
  invariantId: string | null;
  acknowledged: boolean;
  createdAtIso: string;
}
export interface ForgeRunSummary {
  scenariosRun: number;
  scenariosPassed: number;
  scenariosFailed: number;
  invariantChecks: number;
  invariantFailures: number;
  findings: number;
  criticalFindings: number;
  state: ForgeRunState;
  hash: string;
}
export interface ForgeRunResult {
  run: ForgeRunRecord;
  summary: ForgeRunSummary;
  findings: ForgeFindingRecord[];
}

export type LaunchGateStatus = "pass" | "outstanding";
export interface LaunchReadinessGate {
  id: string;
  title: string;
  status: LaunchGateStatus;
  detail: string;
}
export interface LaunchReadinessView {
  environment: string;
  gates: LaunchReadinessGate[];
  allGatesPass: boolean;
  /** Always false: launch sign-off is human-only and never automated here. */
  signedOff: false;
  note: string;
}

export type InvariantSeverity = "critical" | "high" | "medium" | "low";
export type InvariantOutcome = "PASS" | "FAIL" | "SKIPPED" | "NOT_APPLICABLE";
export interface SafetyInvariant {
  id: string;
  category: string;
  severity: InvariantSeverity;
  description: string;
  phase: string;
}
export interface InvariantCheckResult {
  invariantId: string;
  outcome: InvariantOutcome;
  detail: string;
}
export interface SecurityRegressionSummary {
  invariants: SafetyInvariant[];
  latestRun: {
    id: string;
    state: ForgeRunState;
    invariantChecks: number;
    invariantFailures: number;
    completedAtIso: string | null;
  } | null;
  latestChecks: InvariantCheckResult[];
  regressionPassed: boolean;
}

export type ObservabilityPostureState =
  | "operational"
  | "degraded"
  | "partial_outage"
  | "major_outage"
  | "maintenance"
  | "unknown";
export interface ObservabilityComponentState {
  component: string;
  state: ObservabilityPostureState;
}
export interface ObservabilityMetricSample {
  name: string;
  labels: Record<string, string>;
  value: number;
  kind: "counter" | "gauge";
}
export interface SloStatus {
  name: string;
  successRatio: number;
  objective: number;
  errorBudgetRemaining: number;
  breached: boolean;
}
export interface SafeObservabilityView {
  posture: ObservabilityPostureState;
  components: ObservabilityComponentState[];
  metrics: ObservabilityMetricSample[];
  slos: SloStatus[];
  version: string | null;
}

// Read-only platform-admin Forge surface. There is deliberately NO createRun,
// approve, or sign-off call here — starting a run and signing off a launch are
// out-of-band, human-only actions.
const forge = {
  launchReadiness: () => request<LaunchReadinessView>("/api/v1/forge/launch-readiness"),
  securityRegression: () => request<SecurityRegressionSummary>("/api/v1/forge/security-regression"),
  listRuns: () => request<{ runs: ForgeRunRecord[] }>("/api/v1/forge/runs"),
  getRun: (runId: string) => request<ForgeRunResult>(`/api/v1/forge/runs/${runId}`),
  listFindings: (runId: string) => request<{ findings: ForgeFindingRecord[] }>(`/api/v1/forge/runs/${runId}/findings`),
};

// ── Part 1.4: folding duplicate accounts into one ───────────────────────────
//
// Signing in with Google when you first signed in with Discord creates a second
// ODIN account, and the person owns two of everything. Merging fixes that — and
// merging is also, from the server's point of view, indistinguishable from an
// account takeover, so it is a platform-operator action rather than a button a
// customer can press about someone else's account.
//
// These types mirror the server's operator views exactly. `providerSubject` —
// the provider's durable id for a person — is absent from all of them by
// design: nothing about deciding which of two accounts survives needs it, and
// it is the one field worth stealing.

export interface MergeAccountSummary {
  userId: string;
  username: string;
  primaryEmail: string | null;
  accountStatus: string;
  createdAt: string;
  identities: Array<{
    provider: string;
    providerEmail: string | null;
    providerEmailVerified: boolean;
    lastUsedAt: string | null;
  }>;
  workspaces: Array<{ tenantId: string; tenantName: string; role: string; hasPaidSubscription: boolean }>;
}

/**
 * What merging one pair would do, computed by the server and never by the
 * browser. `safe` is the server's verdict; `blockers` is why, when it isn't.
 */
export interface MergePlanView {
  canonicalUserId: string;
  mergedUserId: string;
  canonicalReason: string;
  evidence: { sharedVerifiedEmails: string[]; operatorAsserted: boolean };
  workspacesMoving: Array<{ tenantId: string; tenantName: string; role: string }>;
  workspacesCollapsing: Array<{
    tenantId: string;
    tenantName: string;
    canonicalRole: string;
    mergedRole: string;
    resultingRole: string;
  }>;
  signInMethodsMoving: Array<{ provider: string; providerEmail: string | null }>;
  clearsLegacyDiscordUserId: boolean;
  blockers: Array<{ code: string; message: string }>;
  warnings: string[];
  safe: boolean;
  canonical: MergeAccountSummary;
  merged: MergeAccountSummary;
}

export interface DuplicateCandidateView {
  userIdA: string;
  userIdB: string;
  sharedVerifiedEmails: string[];
  plan: MergePlanView;
}

export interface DuplicateCandidateListView {
  accountsScanned: number;
  candidates: DuplicateCandidateView[];
  safeToMerge: number;
  needingAttention: number;
}

export interface MergeReceiptView {
  version: number;
  mergedAt: string;
  operator: string;
  canonicalUserId: string;
  mergedUserId: string;
  canonicalReason: string;
  evidence: { sharedVerifiedEmails: string[]; operatorAsserted: boolean };
  warnings: string[];
}

export interface MergeResultView {
  status: "merged";
  canonicalUserId: string;
  mergedUserId: string;
  signInMethodsMoved: number;
  workspacesMoved: number;
  workspacesCollapsed: number;
  rolesRaised: number;
  sessionsRevoked: number;
  discordAuthorizationsRevoked: number;
  warnings: string[];
  receipt: MergeReceiptView;
}

export interface MergeRollbackResultView {
  canonicalUserId: string;
  mergedUserId: string;
  identitiesRestored: number;
  membershipsRestored: number;
  membershipsRecreated: number;
  rolesLowered: number;
  softReferencesRestored: number;
  sessionsNotRestored: number;
  notes: string[];
}

export interface MergeRequest {
  userIdA: string;
  userIdB: string;
  /** Only sent when the operator explicitly ticked the override. */
  operatorAssertsOwnership?: boolean;
  /** Only sent when the operator overrode which account survives. */
  canonicalUserId?: string;
}

const accountMerges = {
  /** Every duplicate ODIN currently has, planned but not acted on. */
  candidates: () => request<DuplicateCandidateListView>("/api/v1/admin/account-merges/candidates"),
  /** What merging this pair would do. Changes nothing. */
  preview: (input: MergeRequest) =>
    request<MergePlanView>("/api/v1/admin/account-merges/preview", {
      method: "POST",
      body: JSON.stringify(input),
    }),
  /**
   * Perform the merge. `confirmMergedUserId` is the account that will stop
   * being an account, echoed back from the preview the operator read: the
   * server recomputes the plan and refuses if it now names a different one, so
   * a stale screen cannot merge a pair the wrong way round.
   */
  merge: (input: MergeRequest & { confirmMergedUserId: string }) =>
    request<MergeResultView>("/api/v1/admin/account-merges", {
      method: "POST",
      body: JSON.stringify(input),
    }),
  /** Put a merge back, addressed by the account it decommissioned. */
  rollback: (mergedUserId: string) =>
    request<MergeRollbackResultView>(`/api/v1/admin/account-merges/${mergedUserId}/rollback`, {
      method: "POST",
    }),
};


// ── Part 10: Slack-backed support desk ──────────────────────────────────────
//
// The customer-facing half of the support system. What matters here is what is
// ABSENT: there is no call that sets a priority, no call that assigns an agent,
// no call that attaches an invoice or a refund to a case, and no Slack channel,
// thread id, token or workspace URL anywhere in these types. Priority is derived
// server-side, billing context is resolved server-side, and OKMedia's internal
// Slack coordinates are stripped from every view a customer receives.
//
// `setState` is here because platform staff use the same dashboard; the server
// authorizes it by role, and a customer calling it gets 403.

export type SupportCategory =
  | "ACCOUNT_LOGIN" | "DISCORD_CONNECTION" | "BOT_PERMISSIONS" | "WORKSPACE_MANAGEMENT"
  | "BALDER" | "BILLING" | "CHANGE_PLAN" | "CANCEL_PLAN" | "REFUND" | "PAYMENT_FAILURE"
  | "INVOICE" | "SECURITY_CONCERN" | "OTHER";

export type SupportCaseState =
  | "OPEN" | "AWAITING_SUPPORT" | "AWAITING_CUSTOMER" | "RESOLVED" | "CLOSED";

export type SupportAuthorKind = "CUSTOMER" | "SUPPORT" | "SYSTEM";

export interface SupportCategoryOption {
  code: SupportCategory;
  label: string;
  desk: string;
}

export interface SupportMessageView {
  id: string;
  authorKind: SupportAuthorKind;
  /** True when the signed-in caller wrote it — used only to align the bubble. */
  mine: boolean;
  body: string;
  createdAtIso: string;
}

export interface SupportCaseView {
  id: string;
  /** The short human reference a customer can quote back to us. */
  reference: string;
  subject: string;
  category: SupportCategory;
  categoryLabel: string;
  desk: string;
  priorityLabel: string;
  state: SupportCaseState;
  stateLabel: string;
  stateHint: string;
  awaitingUs: boolean;
  canReply: boolean;
  openedByMe: boolean;
  lastMessageAtIso: string;
  createdAtIso: string;
  closedAtIso: string | null;
  /**
   * Honest wording about the internal hand-off, written by the server. The browser
   * never composes this: whether a message reached OKMedia is a fact the server
   * knows and the customer is entitled to, and inventing reassuring copy here is
   * exactly the failure Part 10.7 forbids.
   */
  deliveryHint: string;
}

export interface SupportCaseDetail {
  supportCase: SupportCaseView;
  messages: SupportMessageView[];
}

const supportBase = (tenantId: string): string => `/api/v1/tenants/${tenantId}/support`;

const support = {
  categories: (tenantId: string) =>
    request<{ categories: SupportCategoryOption[] }>(`${supportBase(tenantId)}/categories`),
  cases: (tenantId: string, options: { openOnly?: boolean } = {}) =>
    request<{ cases: SupportCaseView[] }>(
      `${supportBase(tenantId)}/cases${options.openOnly === true ? "?openOnly=true" : ""}`,
    ),
  case: (tenantId: string, caseId: string) =>
    request<SupportCaseDetail>(`${supportBase(tenantId)}/cases/${encodeURIComponent(caseId)}`),
  /**
   * Open a conversation. Three fields, deliberately: a category, a subject and the
   * customer's own words. Anything else a crafted payload might carry is dropped at
   * the route rather than trusted deeper in.
   */
  openCase: (tenantId: string, input: { category: string; subject: string; body: string }) =>
    request<SupportCaseDetail>(`${supportBase(tenantId)}/cases`, {
      method: "POST",
      body: JSON.stringify(input),
    }),
  /** Reply on your own case. Authorship is decided from the session, not from here. */
  reply: (tenantId: string, caseId: string, body: string) =>
    request<SupportCaseDetail>(`${supportBase(tenantId)}/cases/${encodeURIComponent(caseId)}/messages`, {
      method: "POST",
      body: JSON.stringify({ body }),
    }),
  /** Staff-only on the server. Present here because staff use the same dashboard. */
  setState: (tenantId: string, caseId: string, state: string, note?: string) =>
    request<{ supportCase: SupportCaseView }>(
      `${supportBase(tenantId)}/cases/${encodeURIComponent(caseId)}/state`,
      { method: "POST", body: JSON.stringify(note === undefined ? { state } : { state, note }) },
    ),
};
// ── What ODIN is allowed to do in Discord ───────────────────────────────────
// Bitfields cross this boundary as decimal STRINGS. JSON has no bigint, and the
// standard permission set is far past Number.MAX_SAFE_INTEGER, so parsing one as
// a number would silently round it into a different set of permissions.
export interface DiscordPermissionGroup {
  id: string;
  title: string;
  summary: string;
  /** What the customer loses by unticking this group on Discord's screen. */
  withoutIt: string;
  permissions: string[];
}

export interface DiscordPermissionCatalogue {
  groups: DiscordPermissionGroup[];
  declined: { permission: string; reason: string }[];
  standardBitfield: string;
  administratorBitfield: string;
  administratorRisks: string[];
  standardRationale: string;
}

export type DiscordPermissionTier = "STANDARD" | "ADMINISTRATOR";

export interface DiscordPermissionConsent {
  tier: DiscordPermissionTier;
  acknowledgedAt: string | null;
  acknowledgedByUserId: string | null;
  effectiveBitfield: string;
  catalogue: DiscordPermissionCatalogue;
  canChange: boolean;
  canChooseAdministrator: boolean;
}

const discordPermissions = {
  get: (tenantId: string): Promise<DiscordPermissionConsent> =>
    request<DiscordPermissionConsent>(`/api/v1/tenants/${tenantId}/discord/permissions`),
  /**
   * Sends a tier NAME, never a bitfield. `acknowledgedRisks` is only meaningful
   * for ADMINISTRATOR and the server rejects the choice without an explicit
   * `true`, so a UI that forgets the checkbox fails loudly rather than granting
   * full control quietly.
   */
  choose: (
    tenantId: string,
    tier: DiscordPermissionTier,
    acknowledgedRisks: boolean,
  ): Promise<DiscordPermissionConsent> =>
    request<DiscordPermissionConsent>(`/api/v1/tenants/${tenantId}/discord/permissions`, {
      method: "PUT",
      body: JSON.stringify({ tier, acknowledgedRisks }),
    }),
};

export const api = {
  loginUrl: (redirect = "/app"): string => `${API_BASE}/auth/discord?redirect=${encodeURIComponent(redirect)}`,
  googleLoginUrl: (redirect = "/app/onboarding"): string =>
    `${API_BASE}/api/v1/auth/google/start?redirect=${encodeURIComponent(redirect)}`,
  /**
   * Full-page URL that starts the shared-bot "Connect a server" flow. It must be
   * navigated to directly (window.location) — not fetched — so the session cookie
   * rides along and Discord can redirect the browser back to ODIN.
   *
   * Pass `discordGuildId` whenever the click came from a card that names a
   * server. Discord then pre-selects that server and locks its picker, so a
   * button labelled "Install ODIN in ProJB" cannot install ODIN somewhere else.
   * Leave it out only for the page-level "Connect a server", where the customer
   * has genuinely not chosen yet and the picker is the point.
   */
  sharedConnectUrl: (tenantId: string, discordGuildId?: string): string => {
    const base = `${API_BASE}/api/v1/tenants/${tenantId}/guilds/connect/shared/start`;
    return discordGuildId ? `${base}?guild_id=${encodeURIComponent(discordGuildId)}` : base;
  },
  /** The Discord permission tier this workspace has agreed to, and the catalogue behind it. */
  discordPermissions,
  status: () => request<PlatformStatus>("/api/v1/status"),
  /** Public, secret-free platform status (Phase 8, section 27). */
  publicStatus: () => requestAbsolute<PublicPlatformStatus>(statusUrl),
  // ── Phase 8: artifacts, incidents, turnstile ────────────────────────────────
  // Artifact keys are ALWAYS server-generated; the browser never supplies a key
  // or tenantId beyond the path. No route returns the encryption key, and a
  // download is a short-lived signed URL or a server stream — never a durable URL.
  artifacts,
  // Public incident projection feeds the status page + banner via publicStatus();
  // this operator surface is authorization-gated on the server.
  incidents,
  // Turnstile is ADDITIVE and never replaces auth. The SECRET key stays server-side.
  turnstile,
  session: () => request<{ authenticated: boolean }>("/api/v1/auth/session"),
  me: () => request<Me>("/api/v1/me"),
  // ── Linked accounts ────────────────────────────────────────────────────────
  // Read + link + unlink for the signed-in account's sign-in methods. The server
  // binds every link to the session, refuses to remove the last usable method,
  // and requires a recently authenticated session to remove any of them.
  identities,
  logout: () => request<{ status: string }>("/auth/logout", { method: "POST" }),
  // ── Delete account ─────────────────────────────────────────────────────────
  // Erasure of the person, as opposed to `identities.unlink`, which removes one
  // way of signing in. Kept as its own namespace so the two are never confused
  // at a call site.
  account,
  listWorkspaces: () => request<{ workspaces: Workspace[] }>("/api/v1/tenants"),
  createWorkspace: (name: string) => request<Workspace>("/api/v1/tenants", { method: "POST", body: JSON.stringify({ name }) }),
  readWorkspace: (tenantId: string) => request<Workspace>(`/api/v1/tenants/${tenantId}`),
  renameWorkspace: (tenantId: string, name: string) =>
    request<Workspace>(`/api/v1/tenants/${tenantId}`, { method: "PATCH", body: JSON.stringify({ name }) }),
  workspaceMembers: (tenantId: string) =>
    request<{ members: WorkspaceMember[] }>(`/api/v1/tenants/${tenantId}/members`),
  leaveWorkspace: (tenantId: string) =>
    request<{ status: string }>(`/api/v1/tenants/${tenantId}/leave`, { method: "POST" }),
  /**
   * Read-only, and safe to call as soon as the dialog opens: the customer has to
   * be able to see what deletion costs without having committed to it.
   */
  workspaceDeletePreview: (tenantId: string) =>
    request<WorkspaceDeletionPreview>(`/api/v1/tenants/${tenantId}/delete-preview`),
  /**
   * Irreversible. `confirmName` is the workspace name typed back by the customer;
   * the server rejects anything else, so the browser never decides on its own
   * that a deletion was confirmed.
   */
  deleteWorkspace: (tenantId: string, confirmName: string) =>
    request<WorkspaceDeletionResult>(`/api/v1/tenants/${tenantId}`, {
      method: "DELETE",
      body: JSON.stringify({ confirmName }),
    }),
  listGuilds: () => request<{ guilds: DiscordGuild[] }>("/api/v1/discord/guilds"),
  /** Every server this workspace should see, reconciled against Discord. */
  listServers: (tenantId: string) => request<ServerDirectory>(`/api/v1/tenants/${tenantId}/servers`),
  /**
   * Ask Discord what a server looks like right now.
   *
   * A Discord failure comes back as a normal 200 with `ok: false` and the
   * previous data intact, because "Discord did not answer" is a fact about
   * Discord rather than a broken request — the page keeps what it had and says
   * why it did not change. Only a transport or authorization failure rejects.
   */
  refreshServer: (tenantId: string, guildId: string) =>
    request<GuildRefreshResult>(`/api/v1/tenants/${tenantId}/guilds/${guildId}/refresh`, {
      method: "POST",
    }),
  /**
   * The three ways to stop managing a server, kept as three calls.
   *
   * They are separate on the wire because they are separate promises: one stops
   * ODIN managing the server, one destroys history, one removes the bot from
   * Discord where everyone in the server can see it. A single "remove" endpoint
   * with flags is how those get confused.
   */
  disconnectPreview: (tenantId: string, guildId: string) =>
    request<DisconnectPreview>(`/api/v1/tenants/${tenantId}/guilds/${guildId}/disconnect-preview`),
  disconnectServer: (tenantId: string, guildId: string, options: { deleteData?: boolean } = {}) =>
    request<DisconnectResult>(`/api/v1/tenants/${tenantId}/guilds/${guildId}/disconnect`, {
      method: "POST",
      body: JSON.stringify({ deleteData: options.deleteData === true }),
    }),
  removeBotFromDiscord: (tenantId: string, guildId: string) =>
    request<RemoveFromDiscordResult>(`/api/v1/tenants/${tenantId}/guilds/${guildId}/leave-discord`, {
      method: "POST",
    }),
  connectGuild: (tenantId: string, discordGuildId: string) =>
    request<{ guildId: string; connectionStatus: string }>(`/api/v1/tenants/${tenantId}/guilds/connect`, {
      method: "POST",
      body: JSON.stringify({ discordGuildId }),
    }),
  /**
   * Claim a server ODIN's bot is already in — no trip through Discord.
   *
   * Used by the one card state where the round-trip could only do harm: ODIN can
   * already see its bot in the server, so sending the customer to Discord's
   * "Add to Server" picker asks them to choose a server they have already
   * chosen. The server still verifies presence and the caller's live Discord
   * management permission before it writes anything.
   */
  connectInstalledGuild: (tenantId: string, discordGuildId: string) =>
    request<{ guildId: string; connectionStatus: string; idempotent: boolean }>(
      `/api/v1/tenants/${tenantId}/guilds/connect/installed`,
      { method: "POST", body: JSON.stringify({ discordGuildId }) },
    ),
  listBotInstances: (tenantId: string) => request<{ botInstances: BotInstance[] }>(`/api/v1/tenants/${tenantId}/bot-instances`),
  registerBotInstance: (tenantId: string, input: { discordApplicationId: string; configuredName: string; guildId: string }) =>
    request<BotInstance>(`/api/v1/tenants/${tenantId}/bot-instances`, { method: "POST", body: JSON.stringify(input) }),
  inviteUrl: (tenantId: string, id: string) => request<{ inviteUrl: string }>(`/api/v1/tenants/${tenantId}/bot-instances/${id}/invite`),
  submitToken: (tenantId: string, id: string, token: string) =>
    request<{ instance: BotInstance; validation: { status: string; blockerCount: number; findings: ValidationFinding[] } }>(
      `/api/v1/tenants/${tenantId}/bot-instances/${id}/token`,
      { method: "POST", body: JSON.stringify({ token }) },
    ),
  validateBotInstance: (tenantId: string, id: string) =>
    request<{ instance: BotInstance; validation: { status: string; blockerCount: number; findings: ValidationFinding[] } }>(
      `/api/v1/tenants/${tenantId}/bot-instances/${id}/validate`,
      { method: "POST" },
    ),

  // ── Phase 3: templates ──────────────────────────────────────────────────────
  listTemplates: () => request<{ templates: TemplateSummary[] }>("/api/v1/templates"),
  getTemplate: (templateId: string) => request<TemplateDetail>(`/api/v1/templates/${templateId}`),

  // ── Phase 3: blueprints ─────────────────────────────────────────────────────
  listBlueprints: (tenantId: string) => request<{ blueprints: BlueprintView[] }>(`/api/v1/tenants/${tenantId}/blueprints`),
  createBlueprint: (tenantId: string, input: { name: string; description?: string; communityType?: string }) =>
    request<BlueprintView>(`/api/v1/tenants/${tenantId}/blueprints`, { method: "POST", body: JSON.stringify(input) }),
  getBlueprint: (tenantId: string, blueprintId: string) =>
    request<BlueprintView>(`/api/v1/tenants/${tenantId}/blueprints/${blueprintId}`),
  renameBlueprint: (tenantId: string, blueprintId: string, name: string) =>
    request<BlueprintView>(`/api/v1/tenants/${tenantId}/blueprints/${blueprintId}`, { method: "PATCH", body: JSON.stringify({ name }) }),
  importSnapshot: (tenantId: string, input: { snapshotId: string; name?: string }) =>
    request<ImportOutcome>(`/api/v1/tenants/${tenantId}/blueprints/import-snapshot`, { method: "POST", body: JSON.stringify(input) }),

  // ── Phase 3: blueprint versions ─────────────────────────────────────────────
  createVersion: (tenantId: string, blueprintId: string, input: { content: unknown; notes?: string }) =>
    request<BlueprintVersion>(`/api/v1/tenants/${tenantId}/blueprints/${blueprintId}/versions`, {
      method: "POST",
      body: JSON.stringify(input),
    }),
  getVersion: (tenantId: string, blueprintId: string, versionId: string) =>
    request<BlueprintVersion>(`/api/v1/tenants/${tenantId}/blueprints/${blueprintId}/versions/${versionId}`),
  validateVersion: (tenantId: string, blueprintId: string, versionId: string) =>
    request<ValidationOutcome>(`/api/v1/tenants/${tenantId}/blueprints/${blueprintId}/versions/${versionId}/validate`, { method: "POST" }),
  publishVersion: (tenantId: string, blueprintId: string, versionId: string) =>
    request<BlueprintVersion>(`/api/v1/tenants/${tenantId}/blueprints/${blueprintId}/versions/${versionId}/publish`, { method: "POST" }),
  archiveVersion: (tenantId: string, blueprintId: string, versionId: string) =>
    request<BlueprintVersion>(`/api/v1/tenants/${tenantId}/blueprints/${blueprintId}/versions/${versionId}/archive`, { method: "POST" }),

  // ── Phase 3: snapshots (read-only collection) ───────────────────────────────
  listSnapshots: (tenantId: string, guildId: string) =>
    request<{ snapshots: SnapshotSummary[] }>(`/api/v1/tenants/${tenantId}/guilds/${guildId}/snapshots`),
  getSnapshot: (tenantId: string, guildId: string, snapshotId: string) =>
    request<SnapshotDetail>(`/api/v1/tenants/${tenantId}/guilds/${guildId}/snapshots/${snapshotId}`),
  /**
   * `botInstanceId` is optional and stays optional. Naming one is provenance:
   * it records which bot instance the customer believes did the reading. The
   * collection itself always runs on the shared reader, so a caller who has no
   * instance to name must still be able to take a snapshot — the required
   * select on this call was the whole reason the action was unreachable.
   */
  createSnapshot: (tenantId: string, guildId: string, botInstanceId?: string | null) =>
    request<CreatedSnapshot>(`/api/v1/tenants/${tenantId}/guilds/${guildId}/snapshots`, {
      method: "POST",
      body: JSON.stringify(
        typeof botInstanceId === "string" && botInstanceId !== "" ? { botInstanceId } : {},
      ),
    }),

  // ── Phase 3: dry-runs (read-only plan; there is deliberately no apply) ───────
  listDryRuns: (tenantId: string, guildId: string) =>
    request<{ dryRuns: DryRunView[] }>(`/api/v1/tenants/${tenantId}/guilds/${guildId}/dry-runs`),
  getDryRun: (tenantId: string, guildId: string, dryRunId: string) =>
    request<DryRunView>(`/api/v1/tenants/${tenantId}/guilds/${guildId}/dry-runs/${dryRunId}`),
  createDryRun: (tenantId: string, guildId: string, input: { blueprintVersionId: string; snapshotId: string }) =>
    request<DryRunView>(`/api/v1/tenants/${tenantId}/guilds/${guildId}/dry-runs`, { method: "POST", body: JSON.stringify(input) }),

  // ── Phase 3: persona simulation (read-only compute) ─────────────────────────
  runPersonaSimulation: (tenantId: string, guildId: string, input: { snapshotId?: string | null; blueprintVersionId?: string | null }) =>
    request<PersonaSimView>(`/api/v1/tenants/${tenantId}/guilds/${guildId}/persona-simulations`, {
      method: "POST",
      body: JSON.stringify(input),
    }),

  // ── Phase 4: controlled deployment records ──────────────────────────────────
  // Server state is the source of truth. The client only supplies the immutable
  // dry-run plan id — never an operation list — and never a Discord token.
  listDeployments: (tenantId: string, guildId: string) =>
    request<{ deployments: DeploymentView[] }>(`/api/v1/tenants/${tenantId}/guilds/${guildId}/deployments`),
  createDeployment: (tenantId: string, guildId: string, dryRunPlanId: string) =>
    request<DeploymentView>(`/api/v1/tenants/${tenantId}/guilds/${guildId}/deployments`, {
      method: "POST",
      body: JSON.stringify({ dryRunPlanId }),
    }),
  getDeployment: (tenantId: string, deploymentId: string) =>
    request<DeploymentView>(`/api/v1/tenants/${tenantId}/deployments/${deploymentId}`),
  getDeploymentReport: (tenantId: string, deploymentId: string) =>
    request<DeploymentReportView>(`/api/v1/tenants/${tenantId}/deployments/${deploymentId}/report`),

  // ── Phase 4: approval lifecycle (explicit, never inferred) ──────────────────
  requestApproval: (tenantId: string, deploymentId: string) =>
    request<DeploymentDecision>(`/api/v1/tenants/${tenantId}/deployments/${deploymentId}/request-approval`, { method: "POST" }),
  approveDeployment: (tenantId: string, deploymentId: string, note?: string | null) =>
    request<DeploymentDecision>(`/api/v1/tenants/${tenantId}/deployments/${deploymentId}/approve`, {
      method: "POST",
      body: JSON.stringify({ note: note ?? null }),
    }),
  rejectDeployment: (tenantId: string, deploymentId: string, note?: string | null) =>
    request<ApprovalView>(`/api/v1/tenants/${tenantId}/deployments/${deploymentId}/reject`, {
      method: "POST",
      body: JSON.stringify({ note: note ?? null }),
    }),
  revokeApproval: (tenantId: string, deploymentId: string) =>
    request<ApprovalView>(`/api/v1/tenants/${tenantId}/deployments/${deploymentId}/revoke-approval`, { method: "POST" }),
  queueDeployment: (tenantId: string, deploymentId: string) =>
    request<DeploymentView>(`/api/v1/tenants/${tenantId}/deployments/${deploymentId}/queue`, { method: "POST" }),
  cancelDeployment: (tenantId: string, deploymentId: string) =>
    request<DeploymentView>(`/api/v1/tenants/${tenantId}/deployments/${deploymentId}/cancel`, { method: "POST" }),

  // ── Phase 4: rollback (planned as a new controlled step; never executed here) ─
  rollbackPlan: (tenantId: string, deploymentId: string) =>
    request<RollbackPlanView>(`/api/v1/tenants/${tenantId}/deployments/${deploymentId}/rollback-plan`, { method: "POST" }),
  rollbackApprove: (tenantId: string, deploymentId: string) =>
    request<RollbackApprovalView>(`/api/v1/tenants/${tenantId}/deployments/${deploymentId}/rollback-approve`, { method: "POST" }),
  rollbackQueue: (tenantId: string, deploymentId: string) =>
    request<DeploymentView>(`/api/v1/tenants/${tenantId}/deployments/${deploymentId}/rollback-queue`, { method: "POST" }),
  rollbackReport: (tenantId: string, deploymentId: string) =>
    request<RollbackReportView>(`/api/v1/tenants/${tenantId}/deployments/${deploymentId}/rollback-report`),

  // ── Phase 5: BALDER AI companion ────────────────────────────────────────────
  // BALDER is a read/advise assistant. It can inspect a guild, prepare blueprint
  // DRAFTS, and produce dry-run previews — but it NEVER deploys, approves, or
  // mutates a live Discord server. This client deliberately exposes no BALDER
  // "deploy" or "approve" call, and no Discord token ever leaves the browser.
  balder,

  // ── Phase 6A: community onboarding & verification ───────────────────────────
  // Config/record surface only. Drafts, validation and dry-runs never touch
  // Discord; activation binds to a deployment that already cleared Phase 4.
  onboarding,

  // ── Phase 6B: moderation / automod / logging ────────────────────────────────
  // A moderation action is requested through the server's LOCKED mutation
  // boundary (records QUEUED, no live Discord effect). Automod drafts + previews
  // and logging drafts are record/config only; activation binds to a deployment
  // that already cleared Phase 4. No Discord token ever leaves the browser.
  moderation,

  // ── Phase 6C: engagement (tickets, suggestions, polls, scheduled content) ───
  // Config/record surface only. Config drafts + validation never touch Discord;
  // activation binds to a deployment that already cleared Phase 4. Ticket channel
  // creation and message posting run through the LOCKED engagement boundary — no
  // live Discord effect by default. Every count is a real DB value. No Discord
  // token ever leaves the browser.
  engagement,

  // ── Phase 7: integrations, n8n, workflows, dead-letter, inbox ───────────────
  // Record/config + read surface only. Config drafts and `validate`/`test` never
  // send a live request or mutate Discord; `test` is the SAFE validate-and-preview
  // action. Credentials are POSTed once and surface only as metadata (kind / key
  // version / present) — a signing secret or credential value is NEVER returned
  // to the browser. Replay is eligibility-gated and only enqueues a job.
  integrations,

  // ── Phase 9: billing, subscriptions & entitlements ──────────────────────────
  // Read/redirect surface only. The public pricing projection is secret-free; every
  // tenant route is session/tenant authorized on the server and returns SAFE views
  // (no provider ids, no card/bank data, no raw entitlement flags). Checkout and
  // portal return a HOSTED provider URL to redirect to — no publishable/secret key
  // is ever held here, and "active" is only ever shown once the server confirms it.
  billing,

  // ── Phase 10B: ODIN Forge QA, launch readiness & safe platform observability ─
  // PLATFORM-admin, read-only surface. A Forge run is mock + synthetic + locks-
  // engaged and is NEVER a launch authorization; launch sign-off is human-only and
  // out-of-band, so this client exposes no start/approve/sign-off call. The
  // observability view is coarse and NON-identifying — no tenant name/id, secret,
  // payload, or internal host ever crosses this boundary.
  forge,
  observability: () => request<SafeObservabilityView>("/api/v1/observability"),

  // ── Part 1.4: duplicate accounts ───────────────────────────────────────────
  // PLATFORM-operator surface. Reading candidates and previewing a pair change
  // nothing; merging and rolling back are rate-limited, need a session younger
  // than fifteen minutes, and write a receipt into the audit log. A customer
  // calling any of these gets 403 — merging is never self-serve, because "merge
  // that account into mine" is a takeover request unless something outside the
  // browser proves both accounts belong to the same person.
  accountMerges,

  // ── Part 10: Slack-backed support ──────────────────────────────────────────
  // Opening a conversation, reading your own conversations, and replying on them.
  // Delivery to OKMedia's internal Slack workspace happens entirely server-side:
  // no bot token, signing secret, channel id, thread timestamp or workspace URL is
  // reachable from the browser, and there is no call here that could invite anyone
  // into that workspace. A support conversation can carry words. It cannot move
  // money, change a plan, or touch Discord.
  support,
};
